import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Coachmark from './tutorial/Coachmark';

import { originalGameName } from '../utils/gameNameI18n';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Battle.css';
import './Dungeon.css';
import AuthLayeredCard from './AuthLayeredCard';
import BattleArena from './BattleArena';
import ArenaBattleScreen from './ArenaBattleScreen';
import BattleArenaBackground from './BattleArenaBackground';
import {
  formatEfsunValue,
  getEfsunKindForSlotType,
  getEfsunValueColorClass,
  initEfsunConfig, getEfsunTypeLabel } from '../utils/armorEfsunUtils';
import { buildDetailedStatRows, buildEfsunRows } from '../utils/itemStatUtils';
import { formatPlatingRemaining, getActivePlating } from '../utils/ingotPlatingUtils';
import { translateVillageName } from '../utils/translateGameData';
import { spriteAnimDurationMs } from './BattleSprite';
import {
  mergeMonsterBattleState,
  applyKnockbackToInstances,
  damagedMonsterIds,
  maxKnockbackMoveMs,
  maxKnockbackOutMs,
  knockedMonsterIds,
  canMonsterAutoAttack,
  deathAnimDurationMs,
  knockbackAmountPx,
  isKnockbackImmuneMonster,
  isMetinEncounter,
  isSpawnProtected,
  METIN_BREAK_SOUND,
  METIN_DEATH_LEAD_MS,
} from '../utils/battleSprites';
import {
  authButtonStyle,
  authButtonSmallStyle,
  pullMonstersButtonStyle,
  authStatPanelStyle,
  authCardStyle,
} from '../constants/authStyles';
import createBattleSocket from '../battle/battleSocket';
import { useCharacterOptional } from '../context/CharacterContext';
import { redirectToLoginForInvalidSession } from '../utils/authSession';
import { formatSkillLevelForDevelopment } from '../utils/skillLevelUtils';
import { vibrateShort } from '../utils/haptics';
import { playSfxFile } from '../utils/audioSettings';

import {
  buildPersistentSkillEffects,
  buildSkillEffectSheet,
  getPersistentEffectUntilMs,
  isPersistentSkillEffect,
} from '../utils/skillEffectUtils';

import { getSkillUseSound } from '../utils/battleSkillSound';
import { tl } from '../utils/inlineI18n';
import { fetchActiveArenaMatchId } from '../arena/activeMatch';

// Savaş ekranındaki sağ/sol oklarla haritada ilerleme: 100 adımda haritanın sonu (300,300).
const MAP_WALK_STEPS = 100;
const AUTO_PULL_INTERVAL_MS = 5000;
const AUTO_PULL_MAX_FIELD = 3;
const MAP_WALK_UNIT = 300 / MAP_WALK_STEPS;
const mapWalkStepFromPos = (x, y) => Math.max(0, Math.min(MAP_WALK_STEPS, Math.round((Number(x) + Number(y)) / (2 * MAP_WALK_UNIT))));

const BATTLE_TOUR_STEPS = [
  { selector: '.pull-monsters-button', textKey: 'battlePull' },
  { selector: '.attack-button', textKey: 'battleAttack' },
  { selector: '.skill-button', textKey: 'battleSkill' },
  { selector: '.pot-square', textKey: 'battlePot' },
  { selector: '[data-tour="battle-walk"]', textKey: 'battleWalk' },
  { selector: '[data-tour="battle-map-tab"]', textKey: 'battleMapTab' },
];
const ENCOUNTER_TOUR_STEPS = [
  { selector: '[data-tour="encounter-fight"]', textKey: 'encounterFight' },
  { selector: '[data-tour="encounter-decline"]', textKey: 'encounterDecline' },
];

// Kritik / delici / ikisi birden vuruş sesleri (hem oyuncu hem canavar vuruşları).
const HIT_TYPE_SOUNDS = {
  both: '/sounds/hit/kritik_delici.wav?v=2',
  critical: '/sounds/hit/kritik.wav?v=2',
  pierced: '/sounds/hit/delici.wav',
};
const playHitTypeSound = (critical, pierced, volume = 0.6) => {
  const key = critical && pierced ? 'both' : critical ? 'critical' : pierced ? 'pierced' : null;
  if (key) playSfxFile(HIT_TYPE_SOUNDS[key], volume, 'character');
};

// Savaş ekranındaki 4 pot slotu (envanterdeki pot1..pot4 ile aynı).
const BATTLE_POT_SLOTS = ['pot1', 'pot2', 'pot3', 'pot4'];

const NORMAL_ATTACK_SOUND = 'sounds/normal_attack.mp3';
const NORMAL_ATTACK_VOLUME = 0.5;
const LEVEL_UP_SOUND = '/music/character/levelup.mp3';
const MONSTER_AMBIENT_SOUND_INTERVAL_MS = 7000;

const EFSUN_DISPLAY_ORDER = [
  'attack_power',
  'fire_resistance',
  'water_resistance',
  'earth_resistance',
  'magic_resistance',
  'max_hp',
  'max_sp',
  'spell_speed',
  'hp_absorb_damage',
  'sp_absorb_damage',
  'melee_reflect_chance'
];

const EFSUN_I18N_KEYS = {
  attack_power: 'efsunAttackPower',
  fire_resistance: 'efsunFireResistance',
  water_resistance: 'efsunWaterResistance',
  earth_resistance: 'efsunEarthResistance',
  magic_resistance: 'efsunMagicResistance',
  max_hp: 'efsunMaxHp',
  max_sp: 'efsunMaxSp',
  spell_speed: 'efsunSpellSpeed',
  hp_absorb_damage: 'efsunHpAbsorb',
  sp_absorb_damage: 'efsunSpAbsorb',
  melee_reflect_chance: 'efsunMeleeReflect',
};

const INVENTORY_SLOT_KEYS = {
  weapon: 'slotWeapon',
  armor: 'slotArmor',
  helmet: 'slotHelmet',
  shield: 'slotShield',
  earring: 'slotEarring',
  necklace: 'slotNecklace',
  pot1: 'slotPot1',
  pot2: 'slotPot2',
};

const NORMAL_ATTACK_COOLDOWN_BASE = 1.2;
const NORMAL_ATTACK_COOLDOWN_MIN = 0.5;
const CHARACTER_DEFAULT_CRIT_PERCENT = 5;
const CHARACTER_DEFAULT_PIERCE_PERCENT = 5;

const calcNormalAttackCooldownSec = (speedPercent, rageSpeedValue = 0) => {
  const capped = Math.min(100, Math.max(0, speedPercent));
  let sec = NORMAL_ATTACK_COOLDOWN_BASE - (0.7 * capped / 100);
  if (sec < NORMAL_ATTACK_COOLDOWN_MIN) sec = NORMAL_ATTACK_COOLDOWN_MIN;
  if (rageSpeedValue > 0) {
    sec *= (100 - rageSpeedValue) / 100;
    if (sec < NORMAL_ATTACK_COOLDOWN_MIN) sec = NORMAL_ATTACK_COOLDOWN_MIN;
  }
  return Math.round(sec * 10) / 10;
};

const normalizeSkillImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) return url;
  return `/${url}`;
};

const buildEquippedEfsunTotals = (items, getEfsunLabel) => {
  const totals = {};
  (items || []).forEach((item) => {
    (item.efsunlar || []).forEach((efsun) => {
      if (!efsun?.type) return;
      totals[efsun.type] = (totals[efsun.type] || 0) + (Number(efsun.value) || 0);
    });
  });
  return Object.entries(totals)
    .filter(([type, value]) => {
      if (value > 0) return true;
      // Signed efsuns (Zarar/Beceri Artışı) can reduce damage when negative.
      return value < 0 && (type === 'normal_damage_percent' || type === 'skill_damage_percent');
    })
    .sort(([a], [b]) => {
      const ai = EFSUN_DISPLAY_ORDER.indexOf(a);
      const bi = EFSUN_DISPLAY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map(([type, value]) => ({
      type,
      label: getEfsunLabel(type),
      value
    }));
};

function conventionCharacterBattleSprites(characterId, weaponId, armorId) {
  const cid = Number(characterId) || 1;
  const wid = Number(weaponId) || 0;
  const aid = Number(armorId) || 0;
  const reversed = aid > 0;
  const url = `image/battle/characters/${cid}-${wid}-${aid}-${reversed ? 'all-r' : 'all'}.png`;
  const allSprite = {
    url,
    frameWidth: 100,
    frameHeight: 100,
    frameCount: 5,
    sheetHeight: 300,
    fps: 8,
  };
  const fromRow = (frameRow, fps) => ({ ...allSprite, frameRow, fps });
  return {
    characterId: cid,
    weaponId: wid,
    armorId: aid,
    allSprite,
    attackFps: 12,
    deathSprite: fromRow(reversed ? 2 : 0, 8),
    normalSprite: fromRow(reversed ? 0 : 1, 8),
    attackSprite: fromRow(reversed ? 1 : 2, 12),
  };
}

// characterPanelOnly: sadece karakter panelini çizer (arena karakter sekmesi); savaş bağlantısı açılmaz.
const Battle = ({ characterPanelOnly = false } = {}) => {
  const { t } = useTranslation('battle');
  const { t: tc } = useTranslation('common');
  const { t: tg } = useTranslation('gameData');
  const { t: ti } = useTranslation('inventory');
  const { t: td } = useTranslation('dungeon');

  const getEfsunLabel = (type) => (EFSUN_I18N_KEYS[type] ? t(EFSUN_I18N_KEYS[type]) : getEfsunTypeLabel(type));
  const getSlotLabel = (slotType) => {
    if (slotType === 'boots') return t('slotBoots');
    const key = INVENTORY_SLOT_KEYS[slotType];
    return key ? ti(key) : slotType;
  };
  const formatMarketBuffRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return t('timeExpired');
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (days > 0) return t('timeDaysHours', { days, hours });
    if (hours > 0) return t('timeHoursMinutes', { hours, minutes });
    if (minutes > 0) return t('timeMinutesSeconds', { minutes, seconds: secs });
    return t('timeSeconds', { seconds: secs });
  };
  const skillActiveSuffix = (active) => (active ? t('skillActiveSuffix') : '');
  const skillRangeText = (min, max) => (min != null ? `${min} - ${max}` : t('skillRangeDash'));

  const [userCharacter, setUserCharacter] = useState(null);
  const [activeTab, setActiveTab] = useState('battle');
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [mapHintSeen, setMapHintSeen] = useState(() => {
    try { return localStorage.getItem('battleMapHintSeen') === '1'; } catch { return false; }
  });
  const [mapTrail, setMapTrail] = useState(null);
  const [monsterInstances, setMonsterInstances] = useState([]);
  const [skills, setSkills] = useState([]);
  const [pots, setPots] = useState([]);
  const [villages, setVillages] = useState([]);
  const [equippedItems, setEquippedItems] = useState([]);
  const [characterHP, setCharacterHP] = useState(0);
  const [characterMP, setCharacterMP] = useState(0);
  const [characterMaxHP, setCharacterMaxHP] = useState(0);
  const [characterMaxMP, setCharacterMaxMP] = useState(0);
  const [characterDefense, setCharacterDefense] = useState(0);
  const [dropHistory, setDropHistory] = useState([]);
  const [dropsPopupOpen, setDropsPopupOpen] = useState(false);
  const [encounterOffer, setEncounterOffer] = useState(null);
  const [encounterModalOpen, setEncounterModalOpen] = useState(false);
  const [skillCooldowns, setSkillCooldowns] = useState({});
  const [normalAttackCooldownUntil, setNormalAttackCooldownUntil] = useState(0);
  const [normalAttackRemainingSec, setNormalAttackRemainingSec] = useState(0);
  const [attackBuffUntil, setAttackBuffUntil] = useState(0);
  const [attackBuffBonus, setAttackBuffBonus] = useState(0);
  const [rageSpeedBuffUntil, setRageSpeedBuffUntil] = useState(0);
  const [rageSpeedBuffValue, setRageSpeedBuffValue] = useState(0);
  const [defenseBuffUntil, setDefenseBuffUntil] = useState(0);
  const [defenseBuffValue, setDefenseBuffValue] = useState(0);
  const [fireGhostUntil, setFireGhostUntil] = useState(0);
  const [fireGhostTickDamage, setFireGhostTickDamage] = useState(0);
  const [darkProtectionUntil, setDarkProtectionUntil] = useState(0);
  const [darkProtectionPercent, setDarkProtectionPercent] = useState(0);
  const [darkProtectionDefenseBonus, setDarkProtectionDefenseBonus] = useState(0);
  const [buyuluKeskinlikUntil, setBuyuluKeskinlikUntil] = useState(0);
  const [buyuluKeskinlikAttackBonus, setBuyuluKeskinlikAttackBonus] = useState(0);
  const [buyuluKeskinlikLifestealPercent, setBuyuluKeskinlikLifestealPercent] = useState(0);
  const [buyuluZirhUntil, setBuyuluZirhUntil] = useState(0);
  const [buyuluZirhDefenseBonus, setBuyuluZirhDefenseBonus] = useState(0);
  const [buyuluZirhReflectPercent, setBuyuluZirhReflectPercent] = useState(0);
  const [dehsetUntil, setDehsetUntil] = useState(0);
  const [dehsetReducePercent, setDehsetReducePercent] = useState(0);
  const [expBoostUntil, setExpBoostUntil] = useState(0);
  const [dropBoostUntil, setDropBoostUntil] = useState(0);
  const [lootExpBoostUntil, setLootExpBoostUntil] = useState(0);
  const [lootDropBoostUntil, setLootDropBoostUntil] = useState(0);
  const [expBoostRemainingSec, setExpBoostRemainingSec] = useState(0);
  const [dropBoostRemainingSec, setDropBoostRemainingSec] = useState(0);
  const [lootExpBoostRemainingSec, setLootExpBoostRemainingSec] = useState(0);
  // Ödüllü video ile alınan EXP/Drop bonusları (bitiş: unix ms) ve saniyelik sayaç.
  const [adBoosts, setAdBoosts] = useState({ exp: 0, drop: 0 });
  const [adBoostNow, setAdBoostNow] = useState(() => Date.now());
  const [lootDropBoostRemainingSec, setLootDropBoostRemainingSec] = useState(0);
  const [expShowPercent, setExpShowPercent] = useState(() => {
    try {
      return localStorage.getItem('battleExpShowPercent') === '1';
    } catch {
      return false;
    }
  });
  const [marketBuffPopupOpen, setMarketBuffPopupOpen] = useState(false);
  const [battleBonusesPopupOpen, setBattleBonusesPopupOpen] = useState(false);
  const [fishFoodBuffs, setFishFoodBuffs] = useState([]);
  const [fishFoodBonusTotals, setFishFoodBonusTotals] = useState({});
  const [plantFoodBuffs, setPlantFoodBuffs] = useState([]);
  const [plantFoodBonusTotals, setPlantFoodBonusTotals] = useState({});
  const [fishBuffTick, setFishBuffTick] = useState(0);
  const [buffRemainingSec, setBuffRemainingSec] = useState(0);
  const [loading, setLoading] = useState(true);
  // Bekleyen pot dolumu (backend'den gelir, hacklenemez)
  const [pendingHpRestore, setPendingHpRestore] = useState(0);
  const [pendingMpRestore, setPendingMpRestore] = useState(0);
  const [dropNotifications, setDropNotifications] = useState([]);
  const [dropFlyItems, setDropFlyItems] = useState([]);
  const [characterFloatingDamages, setCharacterFloatingDamages] = useState([]);
  const [characterFloatingManaDamages, setCharacterFloatingManaDamages] = useState([]);
  const [manaBarDamageFloats, setManaBarDamageFloats] = useState([]);
  const [hpBarHealFloats, setHpBarHealFloats] = useState([]);
  const [mpBarHealFloats, setMpBarHealFloats] = useState([]);
  const [expBarGainFloats, setExpBarGainFloats] = useState([]);
  // Seviye atlama (EXP kazanımı) açık/kapalı — kapalıyken öldürmelerden EXP gelmez (sunucu uygular).
  const [expGainEnabled, setExpGainEnabled] = useState(true);
  // EXP kapalıyken kazanılmayan EXP, düğmenin üstünde gri yazıyla belirip içine çekilir.
  const [expBlockedFloats, setExpBlockedFloats] = useState([]);
  const showExpBlocked = (amount) => {
    const n = Number(amount) || 0;
    if (n <= 0) return;
    const id = `exp-blocked-${Date.now()}-${Math.random()}`;
    setExpBlockedFloats((prev) => [...prev.slice(-4), { id, value: n }]);
    setTimeout(() => setExpBlockedFloats((prev) => prev.filter((d) => d.id !== id)), 1300);
  };
  useEffect(() => {
    let alive = true;
    axios.get('/api/battle/exp-gain')
      .then((res) => { if (alive && res.data?.success) setExpGainEnabled(res.data.enabled !== false); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [expGainNotice, setExpGainNotice] = useState(null); // 'on' | 'off' — bilgi penceresi
  useEffect(() => {
    if (!expGainNotice) return undefined;
    const id = setTimeout(() => setExpGainNotice(null), 2600);
    return () => clearTimeout(id);
  }, [expGainNotice]);
  const toggleExpGain = async () => {
    const next = !expGainEnabled;
    setExpGainEnabled(next);
    try {
      const res = await axios.post('/api/battle/exp-gain', { enabled: next });
      if (res.data?.success) {
        const enabled = res.data.enabled !== false;
        setExpGainEnabled(enabled);
        setExpGainNotice(enabled ? 'on' : 'off');
      } else setExpGainEnabled(!next);
    } catch (_) {
      setExpGainEnabled(!next);
    }
  };
  const [brokenSkillImages, setBrokenSkillImages] = useState({});
  const [monsterDamageFloats, setMonsterDamageFloats] = useState({}); // instanceId -> { value, createdAt }
  const [dyingMonsters, setDyingMonsters] = useState([]); // { ...monster, damageDealt, removeAt }
  const [characterBattleSprites, setCharacterBattleSprites] = useState(null);
  const [loadoutLoaded, setLoadoutLoaded] = useState(false);
  const [characterAnim, setCharacterAnim] = useState('normal');
  const [characterDeathActive, setCharacterDeathActive] = useState(false);
  const [skillVisualEffects, setSkillVisualEffects] = useState({});
  const [battleBgOffsetPx, setBattleBgOffsetPx] = useState(0);
  const [characterWalkDir, setCharacterWalkDir] = useState(null);
  const walkTimerRef = useRef(null);
  // Harita oklarına basılı tutunca yarım saniyede bir adım.
  const walkHoldRef = useRef(null);
  const walkFnRef = useRef(null);
  const stopWalkHold = useCallback(() => {
    if (walkHoldRef.current) {
      clearInterval(walkHoldRef.current);
      walkHoldRef.current = null;
    }
  }, []);
  useEffect(() => stopWalkHold, [stopWalkHold]);
  const [battleBgRewinding, setBattleBgRewinding] = useState(false);
  const battleBgMaxOffsetRef = useRef(0);
  const battleBgRewindTimerRef = useRef(null);
  const notificationTimeoutsRef = useRef({});
  const dropFlyTimeoutsRef = useRef({});
  const characterAnchorRef = useRef(null);
  const depotBtnRef = useRef(null);
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const characterCtx = useCharacterOptional();
  const depotItemCount = characterCtx?.depotItemCount ?? 0;
  const setDepotItemCount = characterCtx?.setDepotItemCount;
  const refreshDepotCount = characterCtx?.refreshDepotCount;
  const battleSocketRef = useRef(null);
  // Zindan: durum sunucudan gelir (snapshot / "dungeon" mesajı); istemci yalnızca geri sayar.
  const [dungeon, setDungeon] = useState(null);
  const [dungeonClockOffset, setDungeonClockOffset] = useState(0);
  const [dungeonExitOpen, setDungeonExitOpen] = useState(false);
  const [battleExitOpen, setBattleExitOpen] = useState(false);
  const [dungeonResult, setDungeonResult] = useState(null);
  const dungeonActive = Boolean(dungeon && !dungeon.ended);
  const battleJoinedRef = useRef(false);
  const battleReadyRef = useRef(false);
  const battleAuthedRef = useRef(false);
  const userCharacterRef = useRef(null);
  const prevCharacterLevelRef = useRef(null);
  const levelUpSoundPlayedForRef = useRef(null);
  const joinInFlightRef = useRef(null);
  const lastJoinedBattleRef = useRef(null);
  const battlePositionRef = useRef({ x: 0, y: 0 });
  const battleVillageRef = useRef(null);
  const [battleAuthed, setBattleAuthed] = useState(false);
  const [battleReady, setBattleReady] = useState(false);
  const [battleReconnecting, setBattleReconnecting] = useState(false);
  const monsterInstancesRef = useRef([]);
  const characterDefenseRef = useRef(0); // Şu an backend'de de hesaplanıyor ama ileride kullanabiliriz
  const characterHPRef = useRef(0);
  const characterMPRef = useRef(0);
  const characterMaxHPRef = useRef(0);
  const characterMaxMPRef = useRef(0);
  const mapRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  const knockbackTimersRef = useRef({ outward: null, cleanup: null });
  const deathResetTimerRef = useRef(null);
  const characterDeathActiveRef = useRef(false);
  const skillInFlightRef = useRef(false);
  const battleCombatEpochRef = useRef(0);

  const DROP_FLY_MS = 900;

  const spawnDropFlyAnimation = (imageUrl, staggerIndex = 0) => {
    const normalized = normalizeSkillImageUrl(imageUrl);
    const fromEl = characterAnchorRef.current;
    const toEl = depotBtnRef.current;
    if (!normalized || !fromEl || !toEl) return;

    const launch = () => {
      const fromRect = fromEl.getBoundingClientRect();
      const toRect = toEl.getBoundingClientRect();
      const jitterX = (Math.random() - 0.5) * 28;
      const jitterY = (Math.random() - 0.5) * 16;
      const startX = fromRect.left + fromRect.width / 2 + jitterX;
      const startY = fromRect.top + fromRect.height * 0.35 + jitterY;
      const endX = toRect.left + toRect.width / 2;
      const endY = toRect.top + toRect.height / 2;
      const id = Date.now() + Math.random();

      setDropFlyItems((prev) => [
        ...prev,
        {
          id,
          imageUrl: normalized,
          startX,
          startY,
          deltaX: endX - startX,
          deltaY: endY - startY,
        },
      ]);

      const timeoutId = setTimeout(() => {
        setDropFlyItems((prev) => prev.filter((item) => item.id !== id));
        delete dropFlyTimeoutsRef.current[id];
      }, DROP_FLY_MS + 50);
      dropFlyTimeoutsRef.current[id] = timeoutId;
    };

    if (staggerIndex <= 0) {
      launch();
      return;
    }
    const delayId = setTimeout(launch, staggerIndex * 110);
    dropFlyTimeoutsRef.current[`delay-${Date.now()}-${staggerIndex}`] = delayId;
  };

  const getSkillImageLabel = (url) => {
    if (!url || typeof url !== 'string') return 'skill.png';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'skill.png';
  };

  // Karakter ölünce savaş ekranını sıfırlar; 3 sn sonra canı fulleyip kaldırır
  const handleCharacterDeath = () => {
    if (characterDeathActiveRef.current) return;
    setCharacterHP(0);
    characterHPRef.current = 0;
    characterDeathActiveRef.current = true;
    setCharacterDeathActive(true);
    // Konumu sıfırla
    setPositionX(0);
    setPositionY(0);
    setBattleBgOffsetPx(0);
    setBattleBgRewinding(false);
    if (battleBgRewindTimerRef.current) {
      clearTimeout(battleBgRewindTimerRef.current);
      battleBgRewindTimerRef.current = null;
    }
    // Karakter ölünce canavarlar savaş alanından hemen temizlenir.
    setMonsterInstances([]);
    monsterInstancesRef.current = [];
    // Skill cooldown'larını ve buff'ı sıfırla
    setSkillCooldowns({});
    setNormalAttackCooldownUntil(0);
    setAttackBuffUntil(0);
    setRageSpeedBuffUntil(0);
    setRageSpeedBuffValue(0);
    setAttackBuffBonus(0);
    setDefenseBuffUntil(0);
    setDefenseBuffValue(0);
    setFireGhostUntil(0);
    setFireGhostTickDamage(0);
    setDarkProtectionUntil(0);
    setDarkProtectionPercent(0);
    setDarkProtectionDefenseBonus(0);
    setBuyuluKeskinlikUntil(0);
    setBuyuluKeskinlikAttackBonus(0);
    setBuyuluKeskinlikLifestealPercent(0);
    setBuyuluZirhUntil(0);
    setBuyuluZirhDefenseBonus(0);
    setBuyuluZirhReflectPercent(0);
    setCharacterFloatingDamages([]);
    setCharacterFloatingManaDamages([]);
    setManaBarDamageFloats([]);
    setHpBarHealFloats([]);
    setMpBarHealFloats([]);
    setExpBarGainFloats([]);
    setMonsterDamageFloats({});
    setDyingMonsters([]);
    setPendingHpRestore(0);
    setPendingMpRestore(0);
    setDropsPopupOpen(false);
    setDropHistory([]);
    setCharacterAnim('normal');
    battleJoinedRef.current = false;
    setBattleReady(false);
    if (knockbackTimersRef.current.outward) clearTimeout(knockbackTimersRef.current.outward);
    if (knockbackTimersRef.current.cleanup) clearTimeout(knockbackTimersRef.current.cleanup);
    knockbackTimersRef.current = { outward: null, cleanup: null };

    const socket = battleSocketRef.current;
    if (socket) {
      socket.send('reset', {}, 'event').catch(() => {});
    }

    if (deathResetTimerRef.current) {
      clearTimeout(deathResetTimerRef.current);
    }
    deathResetTimerRef.current = setTimeout(async () => {
      deathResetTimerRef.current = null;
      setMonsterInstances([]);
      setDyingMonsters([]);
      monsterInstancesRef.current = [];
      await fetchCharacterData({ resetHp: true });
      const maxHP = characterMaxHPRef.current;
      if (maxHP > 0) {
        setCharacterHP(maxHP);
        characterHPRef.current = maxHP;
      }
      characterDeathActiveRef.current = false;
      setCharacterDeathActive(false);
      if (ensureBattleJoinedRef.current) {
        await ensureBattleJoinedRef.current();
      }
    }, 3000);
  };

  // characterMaxHP ref'ini güncel tut
  useEffect(() => {
    characterMaxHPRef.current = characterMaxHP;
  }, [characterMaxHP]);
  useEffect(() => {
    characterMaxMPRef.current = characterMaxMP;
  }, [characterMaxMP]);

  // characterHP ref'ini güncel tut
  useEffect(() => {
    characterHPRef.current = characterHP;
  }, [characterHP]);
  useEffect(() => {
    characterDeathActiveRef.current = characterDeathActive;
  }, [characterDeathActive]);
  useEffect(() => {
    characterMPRef.current = characterMP;
  }, [characterMP]);

  // Normal vuruş cooldown sayacı
  useEffect(() => {
    if (normalAttackCooldownUntil <= Date.now()) {
      setNormalAttackRemainingSec(0);
      return;
    }
    // Ekran yalnızca gösterilen saniye değişince yeniden çizilir (100 ms'de bir değil).
    let lastRem = -1;
    const update = () => {
      const rem = Math.max(0, Math.ceil((normalAttackCooldownUntil - Date.now()) / 1000));
      if (rem === lastRem) return;
      lastRem = rem;
      setNormalAttackRemainingSec(rem);
    };
    update();
    const id = setInterval(update, 100);
    return () => clearInterval(id);
  }, [normalAttackCooldownUntil]);

  // Skill cooldown sayacı (her skill için saniye güncellemesi için)
  const [, setSkillCooldownTick] = useState(0);
  useEffect(() => {
    const hasAnyCooldown = Object.values(skillCooldowns).some(until => until > Date.now());
    if (!hasAnyCooldown) return;
    // Ekran sadece gösterilen kalan saniye değişince yeniden çizilir. Eskiden her 100 ms'de
    // tüm savaş ekranı baştan çiziliyordu; bu, beceri/vuruş tepkisini geciktiriyordu.
    let lastSig = '';
    const id = setInterval(() => {
      const now = Date.now();
      const sig = Object.values(skillCooldowns)
        .map((until) => Math.max(0, Math.ceil((until - now) / 1000)))
        .join(',');
      if (sig === lastSig) return;
      lastSig = sig;
      setSkillCooldownTick(t => t + 1);
    }, 100);
    return () => clearInterval(id);
  }, [skillCooldowns]);

  // Aktif buff sürelerine göre kalıcı skill efektlerini senkronize et
  useEffect(() => {
    const persistent = buildPersistentSkillEffects(skills, {
      attackBuffUntil,
      rageSpeedBuffUntil,
      defenseBuffUntil,
      fireGhostUntil,
      darkProtectionUntil,
      buyuluKeskinlikUntil,
      buyuluZirhUntil,
      dehsetUntil,
    });
    setSkillVisualEffects((prev) => {
      const next = { ...prev };
      Object.entries(persistent).forEach(([skillId, eff]) => {
        next[skillId] = {
          ...eff,
          key: prev[skillId]?.key || eff.key,
        };
      });
      Object.keys(next).forEach((skillId) => {
        if (next[skillId].loop && !persistent[skillId]) {
          delete next[skillId];
        }
      });
      return next;
    });
  }, [
    skills,
    attackBuffUntil,
    rageSpeedBuffUntil,
    defenseBuffUntil,
    fireGhostUntil,
    darkProtectionUntil,
    buyuluKeskinlikUntil,
    buyuluZirhUntil,
    dehsetUntil,
  ]);

  // Rüzgâr Çeliği buff kalan süre sayacı
  useEffect(() => {
    if (attackBuffUntil <= Date.now()) {
      setBuffRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((attackBuffUntil - Date.now()) / 1000);
      setBuffRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [attackBuffUntil]);

  // Kızıl Hiddet buff kalan süre sayacı
  const [rageBuffRemainingSec, setRageBuffRemainingSec] = useState(0);
  useEffect(() => {
    if (rageSpeedBuffUntil <= Date.now()) {
      setRageBuffRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((rageSpeedBuffUntil - Date.now()) / 1000);
      setRageBuffRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [rageSpeedBuffUntil]);

  useEffect(() => {
    if (!fishFoodBuffs.length && !plantFoodBuffs.length) return undefined;
    const id = setInterval(() => setFishBuffTick((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, [fishFoodBuffs.length, plantFoodBuffs.length]);

  const activeFishFoodBuffs = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return (fishFoodBuffs || [])
      .filter((buff) => Number(buff.until) > now)
      .map((buff) => ({
        ...buff,
        remainingSec: Number(buff.until) - now,
      }));
  }, [fishFoodBuffs, fishBuffTick]);

  const activePlantFoodBuffs = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return (plantFoodBuffs || [])
      .filter((buff) => Number(buff.until) > now)
      .map((buff) => ({
        ...buff,
        remainingSec: Number(buff.until) - now,
      }));
  }, [plantFoodBuffs, fishBuffTick]);

  const [fireGhostRemainingSec, setFireGhostRemainingSec] = useState(0);
  useEffect(() => {
    if (fireGhostUntil <= Date.now()) {
      setFireGhostRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((fireGhostUntil - Date.now()) / 1000);
      setFireGhostRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [fireGhostUntil]);

  const [darkProtectionRemainingSec, setDarkProtectionRemainingSec] = useState(0);
  useEffect(() => {
    if (darkProtectionUntil <= Date.now()) {
      setDarkProtectionRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((darkProtectionUntil - Date.now()) / 1000);
      setDarkProtectionRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [darkProtectionUntil]);

  const [buyuluKeskinlikRemainingSec, setBuyuluKeskinlikRemainingSec] = useState(0);
  useEffect(() => {
    if (buyuluKeskinlikUntil <= Date.now()) {
      setBuyuluKeskinlikRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((buyuluKeskinlikUntil - Date.now()) / 1000);
      setBuyuluKeskinlikRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [buyuluKeskinlikUntil]);

  const [buyuluZirhRemainingSec, setBuyuluZirhRemainingSec] = useState(0);
  useEffect(() => {
    if (buyuluZirhUntil <= Date.now()) {
      setBuyuluZirhRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((buyuluZirhUntil - Date.now()) / 1000);
      setBuyuluZirhRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [buyuluZirhUntil]);

  const [dehsetRemainingSec, setDehsetRemainingSec] = useState(0);
  useEffect(() => {
    if (dehsetUntil <= Date.now()) {
      setDehsetRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((dehsetUntil - Date.now()) / 1000);
      setDehsetRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [dehsetUntil]);

  useEffect(() => {
    if (expBoostUntil <= Date.now()) {
      setExpBoostRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((expBoostUntil - Date.now()) / 1000);
      setExpBoostRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expBoostUntil]);

  useEffect(() => {
    if (dropBoostUntil <= Date.now()) {
      setDropBoostRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((dropBoostUntil - Date.now()) / 1000);
      setDropBoostRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [dropBoostUntil]);

  useEffect(() => {
    if (lootExpBoostUntil <= Date.now()) {
      setLootExpBoostRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((lootExpBoostUntil - Date.now()) / 1000);
      setLootExpBoostRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [lootExpBoostUntil]);

  useEffect(() => {
    if (lootDropBoostUntil <= Date.now()) {
      setLootDropBoostRemainingSec(0);
      return;
    }
    const update = () => {
      const rem = Math.ceil((lootDropBoostUntil - Date.now()) / 1000);
      setLootDropBoostRemainingSec(rem <= 0 ? 0 : rem);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [lootDropBoostUntil]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    refreshDepotCount?.();
    axios.get('/api/villages')
      .then((res) => {
        if (res?.data?.success) {
          setVillages(res.data.villages || []);
        }
      })
      .catch(() => {});
    axios.get('/api/efsun/config')
      .then((res) => {
        if (res?.data?.success && res.data.config) {
          initEfsunConfig(res.data.config);
        }
      })
      .catch(() => {});
  }, [username, navigate]);

  const fetchExpProgress = async () => {
    try {
      const response = await axios.get(`/api/user-character/exp-progress?username=${encodeURIComponent(username)}`);
      if (!response.data?.success) return;
      const data = response.data;
      setUserCharacter((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          level: data.level ?? prev.level,
          exp: data.exp ?? prev.exp,
          gold: data.gold ?? prev.gold,
          statPoints: data.statPoints ?? prev.statPoints,
          skillPoints: data.skillPoints ?? prev.skillPoints,
          currentLevelExp: data.currentLevelExp ?? prev.currentLevelExp,
          expToNextLevel: data.expToNextLevel ?? prev.expToNextLevel,
          expProgress: data.expProgress ?? prev.expProgress,
          maxHp: data.maxHp ?? prev.maxHp,
          maxMp: data.maxMp ?? prev.maxMp,
        };
      });
      if (typeof data.maxHp === 'number' && data.maxHp > 0) {
        setCharacterMaxHP(data.maxHp);
        characterMaxHPRef.current = data.maxHp;
      }
      if (typeof data.maxMp === 'number' && data.maxMp > 0) {
        setCharacterMaxMP(data.maxMp);
        characterMaxMPRef.current = data.maxMp;
      }
    } catch (err) {
      console.error('Error fetching exp progress:', err);
    }
  };

  const applyCharacterFromServer = (char, { refillMp = false, resetHp = false, previousLevel = null } = {}) => {
    const newLevel = char.level || 1;
    const leveledUp = previousLevel != null && newLevel > previousLevel;
    const maxHP = char.maxHp || char.hp || 5;
    const maxMP = char.maxMp || char.mp || 5;

    let restoredHp;
    if (resetHp) {
      restoredHp = maxHP;
    } else if (characterDeathActiveRef.current) {
      const raw = typeof char.currentHp === 'number' ? char.currentHp : 0;
      restoredHp = Math.max(0, Math.min(raw, maxHP));
    } else if (leveledUp) {
      restoredHp = maxHP;
    } else {
      const raw = typeof char.currentHp === 'number' ? char.currentHp : maxHP;
      restoredHp = raw <= 0 ? maxHP : Math.min(raw, maxHP);
    }
    if (!characterDeathActiveRef.current || resetHp) {
      setCharacterHP(restoredHp);
      characterHPRef.current = restoredHp;
    }
    setCharacterMP(refillMp ? maxMP : Math.min(char.mp ?? maxMP, maxMP));
    setPendingHpRestore(char.pendingHpRestore || 0);
    setPendingMpRestore(char.pendingMpRestore || 0);
    setCharacterMaxHP(maxHP);
    setCharacterMaxMP(maxMP);
    characterMaxHPRef.current = maxHP;
    characterMaxMPRef.current = maxMP;

    const nowSec = Date.now() / 1000;
    if (char.attackBuffUntil > nowSec) {
      setAttackBuffUntil(char.attackBuffUntil * 1000);
      setAttackBuffBonus(char.attackBuffValue || 0);
    } else {
      setAttackBuffUntil(0);
      setAttackBuffBonus(0);
    }
    if (char.rageSpeedBuffUntil > nowSec) {
      setRageSpeedBuffUntil(char.rageSpeedBuffUntil * 1000);
      setRageSpeedBuffValue(char.rageSpeedBuffValue || 0);
    } else {
      setRageSpeedBuffUntil(0);
      setRageSpeedBuffValue(0);
    }
    if (char.defenseBuffUntil > nowSec) {
      setDefenseBuffUntil(char.defenseBuffUntil * 1000);
      setDefenseBuffValue(char.defenseBuffValue || 0);
    } else {
      setDefenseBuffUntil(0);
      setDefenseBuffValue(0);
    }
    if (char.fireGhostUntil > nowSec) {
      setFireGhostUntil(char.fireGhostUntil * 1000);
      setFireGhostTickDamage(char.fireGhostTickDamage || 0);
    } else {
      setFireGhostUntil(0);
      setFireGhostTickDamage(0);
    }
    if (char.darkProtectionUntil > nowSec) {
      setDarkProtectionUntil(char.darkProtectionUntil * 1000);
      setDarkProtectionPercent(Number(char.darkProtectionPercent || 0));
      setDarkProtectionDefenseBonus(Number(char.darkProtectionDefenseBonus || 0));
    } else {
      setDarkProtectionUntil(0);
      setDarkProtectionPercent(0);
      setDarkProtectionDefenseBonus(0);
    }
    if (char.buyuluKeskinlikUntil > nowSec) {
      setBuyuluKeskinlikUntil(char.buyuluKeskinlikUntil * 1000);
      setBuyuluKeskinlikAttackBonus(Number(char.buyuluKeskinlikAttackBonus || 0));
      setBuyuluKeskinlikLifestealPercent(Number(char.buyuluKeskinlikLifestealPercent || 0));
    } else {
      setBuyuluKeskinlikUntil(0);
      setBuyuluKeskinlikAttackBonus(0);
      setBuyuluKeskinlikLifestealPercent(0);
    }
    if (char.buyuluZirhUntil > nowSec) {
      setBuyuluZirhUntil(char.buyuluZirhUntil * 1000);
      setBuyuluZirhDefenseBonus(Number(char.buyuluZirhDefenseBonus || 0));
      setBuyuluZirhReflectPercent(Number(char.buyuluZirhReflectPercent || 0));
    } else {
      setBuyuluZirhUntil(0);
      setBuyuluZirhDefenseBonus(0);
      setBuyuluZirhReflectPercent(0);
    }
    if (char.dehsetUntil > nowSec) {
      setDehsetUntil(char.dehsetUntil * 1000);
      setDehsetReducePercent(Number(char.dehsetReducePercent || 0));
    } else {
      setDehsetUntil(0);
      setDehsetReducePercent(0);
    }
    if (char.expBoostUntil > nowSec) {
      setExpBoostUntil(char.expBoostUntil * 1000);
    } else {
      setExpBoostUntil(0);
    }
    if (char.dropBoostUntil > nowSec) {
      setDropBoostUntil(char.dropBoostUntil * 1000);
    } else {
      setDropBoostUntil(0);
    }
    if (char.lootExpBoostUntil > nowSec) {
      setLootExpBoostUntil(char.lootExpBoostUntil * 1000);
    } else {
      setLootExpBoostUntil(0);
    }
    if (char.lootDropBoostUntil > nowSec) {
      setLootDropBoostUntil(char.lootDropBoostUntil * 1000);
    } else {
      setLootDropBoostUntil(0);
    }
    setFishFoodBuffs(Array.isArray(char.fishFoodBuffs) ? char.fishFoodBuffs : []);
    setFishFoodBonusTotals(char.fishFoodBonusTotals || {});
    setPlantFoodBuffs(Array.isArray(char.plantFoodBuffs) ? char.plantFoodBuffs : []);
    setPlantFoodBonusTotals(char.plantFoodBonusTotals || {});

    if (char.lastNormalAttackAt > 0 && char.normalAttackCooldown > 0) {
      const cooldownEndMs = char.lastNormalAttackAt + char.normalAttackCooldown * 1000;
      if (cooldownEndMs > Date.now()) {
        setNormalAttackCooldownUntil(cooldownEndMs);
      }
    }
  };

  const applyBattleLoadout = (allEquipped) => {
    if (!Array.isArray(allEquipped)) return;
    setEquippedItems(allEquipped);
    const potItems = allEquipped.filter((item) => BATTLE_POT_SLOTS.includes(item.slotType));
    setPots(potItems);
    let totalDefense = 0;
    allEquipped.forEach((item) => {
      if (item.defense) {
        totalDefense += item.defense;
      }
    });
    setCharacterDefense(totalDefense);
    setLoadoutLoaded(true);
  };

  const fetchBattleLoadout = async ({ force = false } = {}) => {
    try {
      const skillsResponse = await axios.get(`/api/battle/skills?username=${username}`);
      if (skillsResponse.data.success) {
        setSkills(skillsResponse.data.skills || []);
      }

      const forceParam = force ? '&force=1' : '';
      const equipmentResponse = await axios.get(
        `/api/equipped-items?username=${username}${forceParam}`
      );
      if (equipmentResponse.data.success) {
        applyBattleLoadout(equipmentResponse.data.items || []);
      }
    } catch (err) {
      console.error('Error fetching battle loadout:', err);
    }
  };

  const fetchCharacterData = async (opts = {}) => {
    const {
      refillMp = false,
      resetHp = false,
      includeLoadout = true,
      syncCombat = false,
    } = typeof opts === 'boolean' ? { refillMp: opts } : opts;
    if (deathResetTimerRef.current && !resetHp) return;
    const reqId = ++fetchRequestIdRef.current;
    try {
      const params = new URLSearchParams({ username });
      if (syncCombat || refillMp || resetHp) params.set('syncCombat', '1');
      if (refillMp) params.set('refillMp', '1');
      if (resetHp) params.set('resetHp', '1');
      const url = `/api/user-character?${params.toString()}`;
      const response = await axios.get(url);
      if (deathResetTimerRef.current && !resetHp) return;
      if (response.data.success && response.data.hasCharacter) {
        if (reqId !== fetchRequestIdRef.current) return;
        const char = response.data.character;
        setUserCharacter(char);
        applyCharacterFromServer(char, {
          refillMp,
          resetHp,
          previousLevel: userCharacter?.level,
        });

        if (includeLoadout) {
          await fetchBattleLoadout({ force: syncCombat || refillMp || resetHp });
        }
      }
    } catch (err) {
      console.error('Error fetching character data:', err);
    } finally {
      setLoading(false);
    }
  };

  // İlk yükleme: ağır /api/user-character bekleme; context veya kısa timeout ile aç
  useEffect(() => {
    if (!username) return;

    let cancelled = false;
    const safetyTimer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 2500);

    const applyFromContext = () => {
      const char = characterCtx?.character;
      if (!char || cancelled) return false;
      setUserCharacter(char);
      applyCharacterFromServer(char);
      setLoading(false);
      clearTimeout(safetyTimer);
      return true;
    };

    if (applyFromContext()) {
      fetchBattleLoadout({ force: true });
      return () => {
        cancelled = true;
        clearTimeout(safetyTimer);
      };
    }

    if (characterCtx && !characterCtx.loading) {
      setLoading(false);
      clearTimeout(safetyTimer);
      fetchBattleLoadout({ force: true });
    }

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
    };
  }, [username, characterCtx?.character, characterCtx?.loading]);

  const equippedWeapon = equippedItems.find((item) => item.slotType === 'weapon');
  const equippedArmor = equippedItems.find((item) => item.slotType === 'armor');
  const equippedEfsunTotals = buildEquippedEfsunTotals(equippedItems, getEfsunLabel);
  const equippedWeaponSpriteId = equippedWeapon?.baseItemId ?? 0;
  const equippedArmorSpriteId = equippedArmor?.baseItemId ?? 0;

  useEffect(() => {
    if (!loadoutLoaded || !userCharacter?.characterId) return;
    let cancelled = false;

    const fetchCharacterSprites = async () => {
      try {
        const spriteRes = await axios.get(
          `/api/battle/character-sprites?characterId=${userCharacter.characterId}&weaponId=${equippedWeaponSpriteId}&armorId=${equippedArmorSpriteId}`
        );
        if (!cancelled && spriteRes.data?.success) {
          setCharacterBattleSprites(
            spriteRes.data.sprites ||
              conventionCharacterBattleSprites(
                userCharacter.characterId,
                equippedWeaponSpriteId,
                equippedArmorSpriteId
              )
          );
        }
      } catch (spriteErr) {
        if (!cancelled) {
          console.error('Error fetching battle character sprites:', spriteErr);
          setCharacterBattleSprites(
            conventionCharacterBattleSprites(
              userCharacter.characterId,
              equippedWeaponSpriteId,
              equippedArmorSpriteId
            )
          );
        }
      }
    };

    fetchCharacterSprites();
    return () => {
      cancelled = true;
    };
  }, [loadoutLoaded, userCharacter?.characterId, equippedWeaponSpriteId, equippedArmorSpriteId]);

  // Sağ üst bildirim: type 'item' = düşen item (yeşil), varsayılan 'warning' = uyarı (sarı)
  const addDropNotification = (message, type = 'warning') => {
    const id = Date.now() + Math.random();
    // En fazla 3 bildirim tut; 4. gelirse en eskiyi düşür
    setDropNotifications(prev => {
      const next = [...prev, { id, message, type }];
      if (next.length <= 3) return next;
      const removed = next.shift();
      if (removed?.id && notificationTimeoutsRef.current[removed.id]) {
        clearTimeout(notificationTimeoutsRef.current[removed.id]);
        delete notificationTimeoutsRef.current[removed.id];
      }
      return next;
    });
    
    // 3 saniye sonra bildirimi sil
    const timeoutId = setTimeout(() => {
      setDropNotifications(prev => prev.filter(n => n.id !== id));
      delete notificationTimeoutsRef.current[id];
    }, 3000);
    
    notificationTimeoutsRef.current[id] = timeoutId;
  };

  useEffect(() => {
    const knockbackTimers = knockbackTimersRef.current;
    return () => {
      // Bildirim timeout'larını temizle
      Object.values(notificationTimeoutsRef.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      notificationTimeoutsRef.current = {};
      Object.values(dropFlyTimeoutsRef.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
      dropFlyTimeoutsRef.current = {};
      if (knockbackTimers.outward) clearTimeout(knockbackTimers.outward);
      if (knockbackTimers.cleanup) clearTimeout(knockbackTimers.cleanup);
      if (deathResetTimerRef.current) clearTimeout(deathResetTimerRef.current);
    };
  }, []);

  // monsterInstances ve characterDefense ref'lerini güncelle
  useEffect(() => {
    monsterInstancesRef.current = monsterInstances;
  }, [monsterInstances]);

  // Canavar sesleri: 7 saniyede bir, sahadaki canavarlardan rastgele biri
  useEffect(() => {
    if (monsterInstances.length === 0 || characterHP <= 0) return undefined;

    const playRandomMonsterSound = () => {
      const instances = monsterInstancesRef.current;
      if (!instances.length) return;
      const monster = instances[Math.floor(Math.random() * instances.length)];
      const sound = monster?.attackSound || 'sounds/monsters/default_attack.mp3';
      playSfxFile(sound, 1, 'character');
    };

    const intervalId = setInterval(playRandomMonsterSound, MONSTER_AMBIENT_SOUND_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [monsterInstances.length, characterHP]);
  
  useEffect(() => {
    characterDefenseRef.current = characterDefense;
  }, [characterDefense]);

  const monsterKey = positionX * positionY;

  const handleMapClick = (event) => {
    if (!mapRef.current) return;
    const rect = mapRef.current.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;
    const normalizedX = Math.max(0, Math.min(300, Math.round((relativeX / rect.width) * 300)));
    const normalizedY = Math.max(0, Math.min(300, Math.round((relativeY / rect.height) * 300)));
    setMapTrail({ fromX: positionX, fromY: positionY, toX: normalizedX, toY: normalizedY, key: Date.now() });
    setPositionX(normalizedX);
    setPositionY(normalizedY);
    if (!mapHintSeen) {
      setMapHintSeen(true);
      try { localStorage.setItem('battleMapHintSeen', '1'); } catch { /* yoksay */ }
    }
  };

  const playCharacterAttack = () => {
    const attackSheet = characterBattleSprites?.attackSprite;
    if (!attackSheet) return;
    setCharacterAnim('attack');
    const ms = spriteAnimDurationMs(attackSheet);
    setTimeout(() => setCharacterAnim('normal'), ms);
  };

  const estimateNormalAttackCooldownSec = () => {
    let sec = 1.2;
    if (rageSpeedBuffUntil > Date.now() && rageSpeedBuffValue > 0) {
      sec *= (100 - rageSpeedBuffValue) / 100;
    }
    return Math.max(0.5, sec);
  };

  const applyOptimisticMeleeFeedback = () => {
    playCharacterAttack();
  };

  const finishMonsterAttackAnims = (onlyIds = null) => {
    setTimeout(() => {
      setMonsterInstances((prev) =>
        prev.map((m) =>
          m.anim === 'attack' && (!onlyIds || onlyIds.has(m.id))
            ? { ...m, anim: 'idle' }
            : m
        )
      );
    }, 520);
  };

  const playMonsterAttackAnims = (onlyIds = null) => {
    setMonsterInstances((prev) =>
      prev.map((m) =>
        canMonsterAutoAttack(m) && (!onlyIds || onlyIds.has(m.id))
          ? { ...m, anim: 'attack' }
          : m
      )
    );
    finishMonsterAttackAnims(onlyIds);
  };

  const scheduleKnockbackReturn = (moveMs, knockedIds = null, outMs = 260) => {
    if (!moveMs || moveMs <= 0) return;
    if (knockbackTimersRef.current.outward) clearTimeout(knockbackTimersRef.current.outward);
    if (knockbackTimersRef.current.cleanup) clearTimeout(knockbackTimersRef.current.cleanup);
    const idSet = knockedIds instanceof Set ? knockedIds : null;
    knockbackTimersRef.current.outward = setTimeout(() => {
      setMonsterInstances((prev) =>
        prev.map((m) => {
          const wasKnocked = idSet ? idSet.has(m.id) : m.knockbackMoveMs > 0;
          if (wasKnocked && m.hp > 0 && m.anim !== 'death') {
            return { ...m, knockbackPx: 0, knockbackUntil: 0, anim: 'run' };
          }
          return { ...m, knockbackPx: 0, knockbackUntil: 0 };
        })
      );
      knockbackTimersRef.current.outward = null;
    }, outMs);
    knockbackTimersRef.current.cleanup = setTimeout(() => {
      setMonsterInstances((prev) =>
        prev.map((m) => {
          const wasKnocked = idSet ? idSet.has(m.id) : m.knockbackMoveMs > 0;
          if (!wasKnocked) return m;
          if (m.hp > 0 && m.anim !== 'death') {
            return { ...m, knockbackMoveMs: 0, knockbackOutMs: 0, anim: 'attack' };
          }
          return { ...m, knockbackMoveMs: 0, knockbackOutMs: 0 };
        })
      );
      if (idSet) finishMonsterAttackAnims(idSet);
      knockbackTimersRef.current.cleanup = null;
    }, outMs + moveMs);
  };

  const handleApproachComplete = (instanceId) => {
    const current = monsterInstancesRef.current.find((m) => m.id === instanceId);
    if (current?.spawnPhase === 'predrop') {
      setMonsterInstances((prev) =>
        prev.map((m) =>
          m.id === instanceId && m.hp > 0 && m.anim !== 'death'
            ? { ...m, spawnPhase: 'dropping' }
            : m
        )
      );
      return;
    }
    const ids = new Set([instanceId]);
    setMonsterInstances((prev) =>
      prev.map((m) =>
        m.id === instanceId && m.hp > 0 && m.anim !== 'death'
          ? { ...m, spawnPhase: 'ready', anim: 'attack' }
          : m.id === instanceId
            ? { ...m, spawnPhase: 'ready' }
            : m
      )
    );
    finishMonsterAttackAnims(ids);
  };

  const handleMonsterDeathComplete = (monster) => {
    setDyingMonsters((prev) =>
      prev.filter((m) => !(m.id === monster.id && m.removeAt === monster.removeAt))
    );
  };
  const strengthValue = userCharacter?.strength || 0;
  const characterLevelValue = userCharacter?.level || 1;
  const agilityValue = userCharacter?.agility || 0;
  const vitValue = userCharacter?.vit ?? 0;
  const intValue = userCharacter?.mpStat || 0;
  const eqStatBonuses = userCharacter?.equipmentStatBonuses || {};
  const totalStrengthValue =
    strengthValue +
    (userCharacter?.efsunStrengthBonus || 0) +
    (eqStatBonuses.strengthBonus || 0) +
    (userCharacter?.storyBonuses?.strength || 0);
  const estimatedBaseMin = strengthValue;
  const estimatedBaseMax = strengthValue + 9;
  const estimatedWeaponMin = equippedWeapon?.attackMin || 0;
  const estimatedWeaponMax = equippedWeapon?.attackMax || estimatedWeaponMin;
  const baseNormalAttackMin = estimatedBaseMin + estimatedWeaponMin;
  const baseNormalAttackMax = estimatedBaseMax + estimatedWeaponMax;

  const getSkillKValue = (level) => {
    const kMap = {
      1: 0.05, 2: 0.06, 3: 0.08, 4: 0.10, 5: 0.12, 6: 0.14, 7: 0.16, 8: 0.18, 9: 0.20, 10: 0.22,
      11: 0.24, 12: 0.26, 13: 0.28, 14: 0.30, 15: 0.32, 16: 0.34,
      17: 0.50, 18: 0.52, 19: 0.54, 20: 0.56, 21: 0.58, 22: 0.60, 23: 0.63, 24: 0.66, 25: 0.69, 26: 0.72,
      27: 0.82, 28: 0.85, 29: 0.88, 30: 0.91, 31: 0.94, 32: 0.98, 33: 1.02, 34: 1.06, 35: 1.10, 36: 1.15,
      37: 1.25,
    };
    return kMap[level] || 0.05;
  };

  const buffSkill = skills.find((skill) => skill.isBuffSkill);
  const ofkeSkill = skills.find((s) => s.isOfke);
  const defenseBuffSkill = skills.find((s) => s.isDefenseBuff);
  const defenseBuffSkillLevel = Math.max(1, Math.min(20, Number(defenseBuffSkill?.level || 1)));
  const defenseBuffK = getSkillKValue(defenseBuffSkillLevel);
  const previewDefenseBonus = defenseBuffSkill
    ? Math.max(0, Math.round((200 + strengthValue * 0.2 + vitValue * 0.5) * defenseBuffK))
    : 0;
  const defenseBuffMana = defenseBuffSkill ? Math.min(355, 91 + Math.floor((defenseBuffSkillLevel - 1) * (355 - 91) / 19)) : 91;
  const defenseBuffDuration = defenseBuffSkill ? Math.min(172, 64 + Math.floor((defenseBuffSkillLevel - 1) * (172 - 64) / 19)) : 64;
  const isDefenseBuffActive = defenseBuffUntil > Date.now() && defenseBuffValue > 0;
  const effectiveDefenseBonus = isDefenseBuffActive ? defenseBuffValue : 0;
  const fireGhostSkill = skills.find((s) => s.isAtesHayaleti);
  const fireGhostSkillLevel = Math.max(1, Math.min(20, Number(fireGhostSkill?.level || 1)));
  const fireGhostInitialMana = fireGhostSkill ? Math.round(21 + (36 * (fireGhostSkillLevel - 1) / 19)) : 21;
  const fireGhostDuration = fireGhostSkill ? Math.round(41 + (36 * (fireGhostSkillLevel - 1) / 19)) : 41;
  const fireGhostTickMana = fireGhostSkill ? Math.round(7 + (48 * (fireGhostSkillLevel - 1) / 19)) : 7;
  const isFireGhostActive = fireGhostUntil > Date.now();
  const darkProtectionSkill = skills.find((s) => s.isKaranlikKoruma);
  const darkProtectionSkillLevel = Math.max(1, Math.min(20, Number(darkProtectionSkill?.level || 1)));
  const darkProtectionInitialMana = darkProtectionSkill ? Math.round(21 + (36 * (darkProtectionSkillLevel - 1) / 19)) : 21;
  const darkProtectionDuration = darkProtectionSkill ? Math.round(66 + (144 * (darkProtectionSkillLevel - 1) / 19)) : 66;
  const darkProtectionTickMana = darkProtectionSkill ? Math.round(5 + (12 * (darkProtectionSkillLevel - 1) / 19)) : 5;
  const isDarkProtectionActive = darkProtectionUntil > Date.now();
  const previewDarkProtectionDefenseBonus = darkProtectionSkill
    ? Math.max(0, Math.round((15 + (userCharacter?.mpStat || 0) * 0.5) * getSkillKValue(darkProtectionSkillLevel)))
    : 0;
  const buyuluKeskinlikSkill = skills.find((s) => s.isBuyuluKeskinlik);
  const buyuluKeskinlikSkillLevel = Math.max(1, Math.min(20, Number(buyuluKeskinlikSkill?.level || 1)));
  const buyuluKeskinlikK = getSkillKValue(buyuluKeskinlikSkillLevel);
  const buyuluKeskinlikInitialMana = buyuluKeskinlikSkill ? Math.round(32 + (288 * (buyuluKeskinlikSkillLevel - 1) / 19)) : 32;
  const buyuluKeskinlikDuration = buyuluKeskinlikSkill ? Math.round(90 + (90 * (buyuluKeskinlikSkillLevel - 1) / 19)) : 90;
  const buyuluKeskinlikTickMana = buyuluKeskinlikSkill ? Math.round(3 + (27 * (buyuluKeskinlikSkillLevel - 1) / 19)) : 3;
  const buyuluKeskinlikPreviewAttackBonus = buyuluKeskinlikSkill
    ? Math.max(0, Math.round((intValue * 3 + buyuluKeskinlikSkillLevel * 2) * buyuluKeskinlikK))
    : 0;
  const buyuluKeskinlikPreviewLifesteal = buyuluKeskinlikSkill ? Number((12 * (buyuluKeskinlikSkillLevel - 1) / 19).toFixed(2)) : 0;
  const isBuyuluKeskinlikActive = buyuluKeskinlikUntil > Date.now();
  const buyuluZirhSkill = skills.find((s) => s.isBuyuluZirh);
  const buyuluZirhSkillLevel = Math.max(1, Math.min(20, Number(buyuluZirhSkill?.level || 1)));
  const buyuluZirhK = getSkillKValue(buyuluZirhSkillLevel);
  const buyuluZirhInitialMana = buyuluZirhSkill ? Math.round(78 + (204 * (buyuluZirhSkillLevel - 1) / 19)) : 78;
  const buyuluZirhDuration = buyuluZirhSkill ? Math.round(36 + (144 * (buyuluZirhSkillLevel - 1) / 19)) : 36;
  const buyuluZirhPreviewDefenseBonus = buyuluZirhSkill
    ? Math.max(0, Math.round((30 + intValue) * buyuluZirhK))
    : 0;
  const buyuluZirhPreviewReflectPercent = buyuluZirhSkill
    ? Math.max(0, Math.min(100, (intValue / 4 + 10) * buyuluZirhK))
    : 0;
  const isBuyuluZirhActive = buyuluZirhUntil > Date.now();
  const isDehsetActive = dehsetUntil > Date.now();
  const ofkeSkillLevel = Math.max(1, Math.min(37, Number(ofkeSkill?.level || 1)));
  const ofkeK = getSkillKValue(ofkeSkillLevel);
  const ofkeSpeedBonus = ofkeSkill ? Math.min(100, Math.round(50 * ofkeK)) : 0;
  const ofkeMana = ofkeSkill ? Math.min(225, 57 + Math.floor((ofkeSkillLevel - 1) * (225 - 57) / 36)) : 57;
  const ofkeDuration = ofkeSkill ? Math.min(172, 64 + Math.floor((ofkeSkillLevel - 1) * (172 - 64) / 36)) : 64;
  const buffSkillLevel = Math.max(1, Math.min(37, Number(buffSkill?.level || 1)));
  const buffK = getSkillKValue(buffSkillLevel);
  const previewBuffAttackBonus = Math.max(
    0,
    Math.round((100 + totalStrengthValue + characterLevelValue * 3) * buffK)
  );
  const previewBuffMana = Math.min(350, Math.max(110, 110 + Math.floor((buffSkillLevel - 1) * (350 - 110) / 36)));
  const previewBuffDuration = Math.min(92, Math.max(32, 32 + Math.floor(((buffSkillLevel - 1) * 60) / 36)));
  const isBuffActive = attackBuffUntil > Date.now() && attackBuffBonus > 0;
  const effectiveBuffBonus = (isBuffActive ? attackBuffBonus : 0) + (isBuyuluKeskinlikActive ? buyuluKeskinlikAttackBonus : 0);

  const displayedNormalAttackMin = baseNormalAttackMin + effectiveBuffBonus;
  const displayedNormalAttackMax = baseNormalAttackMax + effectiveBuffBonus;
  const fishAttackPowerPercent = Number(fishFoodBonusTotals?.attackPowerPercent || 0);
  const fishMaxHpFlat = Number(fishFoodBonusTotals?.maxHpFlat || 0);
  const fishDefensePercent = Number(fishFoodBonusTotals?.defensePercent || 0);
  const fishCriticalPercent = Number(fishFoodBonusTotals?.criticalChancePercent || 0);
  const fishPiercePercent = Number(fishFoodBonusTotals?.pierceChancePercent || 0);
  const fishAttackSpeedPercent = Number(fishFoodBonusTotals?.attackSpeedPercent || 0);
  const fishHpProductionPercent = Number(fishFoodBonusTotals?.hpProductionPercent || 0);
  const fishExperiencePercent = Number(fishFoodBonusTotals?.experiencePercent || 0);
  const plantAttackPowerPercent = Number(plantFoodBonusTotals?.attackPowerPercent || 0);
  const plantMaxHpFlat = Number(plantFoodBonusTotals?.maxHpFlat || 0);
  const plantDefensePercent = Number(plantFoodBonusTotals?.defensePercent || 0);
  const plantDefenseFlat = Number(plantFoodBonusTotals?.defenseFlat || 0);
  const plantCriticalPercent = Number(plantFoodBonusTotals?.criticalChancePercent || 0);
  const plantPiercePercent = Number(plantFoodBonusTotals?.pierceChancePercent || 0);
  const plantAttackSpeedPercent = Number(plantFoodBonusTotals?.attackSpeedPercent || 0);
  const plantHpProductionPercent = Number(plantFoodBonusTotals?.hpProductionPercent || 0);
  const plantExperiencePercent = Number(plantFoodBonusTotals?.experiencePercent || 0);
  const plantSkillDamagePercent = Number(plantFoodBonusTotals?.skillDamagePercent || 0);
  const foodAttackPowerPercent = fishAttackPowerPercent + plantAttackPowerPercent;
  // AP% hem normal hem skill'i etkiler; bitki skillDamagePercent sadece skill
  const foodSkillDamagePercent = foodAttackPowerPercent + plantSkillDamagePercent;
  const foodMaxHpFlat = fishMaxHpFlat + plantMaxHpFlat;
  const foodDefensePercent = fishDefensePercent + plantDefensePercent;
  const foodCriticalPercent = fishCriticalPercent + plantCriticalPercent;
  const foodPiercePercent = fishPiercePercent + plantPiercePercent;
  const foodAttackSpeedPercent = fishAttackSpeedPercent + plantAttackSpeedPercent;
  const foodHpProductionPercent = fishHpProductionPercent + plantHpProductionPercent;
  const foodExperiencePercent = fishExperiencePercent + plantExperiencePercent;
  const efsunValue = (type) => {
    const row = equippedEfsunTotals.find((entry) => entry.type === type);
    return row ? Number(row.value) : 0;
  };
  const efsunCriticalPercent = efsunValue('critical_chance');
  const efsunPiercePercent = efsunValue('pierce_chance');
  const efsunAttackSpeedPercent = efsunValue('attack_speed');
  const efsunNormalDamagePercent = efsunValue('normal_damage_percent');
  const efsunSkillDamagePercent = efsunValue('skill_damage_percent');
  // Kaplama ayrı badge ile gösterilir; burada yiyecek + efsun % toplanır
  const displayedNormalDamagePercent = foodAttackPowerPercent + efsunNormalDamagePercent;
  // Zarar Artışı (normal_damage_percent) sadece normal vuruşu etkiler; skill'e eklenmez
  const displayedSkillDamagePercent = foodSkillDamagePercent + efsunSkillDamagePercent;
  const applyDamagePercent = (value, percent) => {
    if (value == null) return value;
    if (!percent) return value;
    return Math.round(Number(value) * (100 + percent) / 100);
  };
  const weaponAttackSpeedPercent = Number(equippedWeapon?.attackSpeed || 0);
  const equipmentAttackSpeedPercent = Math.min(100, weaponAttackSpeedPercent + efsunAttackSpeedPercent);
  const totalAttackSpeedPercent = Math.min(100, equipmentAttackSpeedPercent + foodAttackSpeedPercent);
  const displayedCriticalPercent = CHARACTER_DEFAULT_CRIT_PERCENT + efsunCriticalPercent + foodCriticalPercent;
  const displayedPiercePercent = CHARACTER_DEFAULT_PIERCE_PERCENT + efsunPiercePercent + foodPiercePercent;
  const rageSpeedBonusActive = rageBuffRemainingSec > 0 && rageSpeedBuffValue > 0;
  const displayedNormalAttackCooldown = calcNormalAttackCooldownSec(
    totalAttackSpeedPercent,
    rageSpeedBonusActive ? rageSpeedBuffValue : 0
  );
  const skillDefenseBonus = effectiveDefenseBonus
    + (isDarkProtectionActive ? darkProtectionDefenseBonus : 0)
    + (isBuyuluZirhActive ? buyuluZirhDefenseBonus : 0);
  const displayedNormalAttackMinWithFish = applyDamagePercent(displayedNormalAttackMin, displayedNormalDamagePercent);
  const displayedNormalAttackMaxWithFish = applyDamagePercent(displayedNormalAttackMax, displayedNormalDamagePercent);

  const kilicCevirmeSkill = skills.find((s) => s.isKilicCevirme);
  const kilicCevirmeLevel = Math.max(1, Math.min(37, Number(kilicCevirmeSkill?.level || 1)));
  const kilicCevirmeK = getSkillKValue(kilicCevirmeLevel);
  const kilicCevirmeAttackMin = strengthValue + estimatedWeaponMin + effectiveBuffBonus;
  const kilicCevirmeAttackMax = strengthValue + 9 + estimatedWeaponMax + effectiveBuffBonus;
  const kilicCevirmeDamageMinBase = Math.max(0, 3 * kilicCevirmeAttackMin + Math.round((0.8 * kilicCevirmeAttackMin + strengthValue * 5 + agilityValue * 3 + vitValue) * kilicCevirmeK));
  const kilicCevirmeDamageMaxBase = Math.max(0, 3 * kilicCevirmeAttackMax + Math.round((0.8 * kilicCevirmeAttackMax + strengthValue * 5 + agilityValue * 3 + vitValue) * kilicCevirmeK));
  const kilicCevirmeDamageMin = applyDamagePercent(kilicCevirmeDamageMinBase, displayedSkillDamagePercent);
  const kilicCevirmeDamageMax = applyDamagePercent(kilicCevirmeDamageMaxBase, displayedSkillDamagePercent);

  const hamleSkill = skills.find((s) => s.isHamle);
  const hamleLevel = Math.max(1, Math.min(37, Number(hamleSkill?.level || 1)));
  const hamleK = getSkillKValue(hamleLevel);
  const hamleAttackMin = strengthValue + estimatedWeaponMin + effectiveBuffBonus;
  const hamleAttackMax = strengthValue + 9 + estimatedWeaponMax + effectiveBuffBonus;
  const hamleDamageFromAttack = (atk) => Math.max(0, 2 * atk + Math.round((atk + strengthValue * 7 + agilityValue * 3 + vitValue) * hamleK));
  const hamleDamageMin = hamleSkill
    ? applyDamagePercent(hamleDamageFromAttack(hamleAttackMin), displayedSkillDamagePercent)
    : null;
  const hamleDamageMax = hamleSkill
    ? applyDamagePercent(hamleDamageFromAttack(hamleAttackMax), displayedSkillDamagePercent)
    : null;

  const ucYonluKesmeSkill = skills.find((s) => s.isUcYonluKesme);
  const ucYonluKesmeLevel = Math.max(1, Math.min(37, Number(ucYonluKesmeSkill?.level || 1)));
  const ucYonluKesmeK = getSkillKValue(ucYonluKesmeLevel);
  const ucYonluKesmeAttackMin = strengthValue + estimatedWeaponMin + effectiveBuffBonus;
  const ucYonluKesmeAttackMax = strengthValue + 9 + estimatedWeaponMax + effectiveBuffBonus;
  const ucYonluKesmeDamageFromAttack = (atk) => Math.max(0, Math.round(1.1 * atk) + Math.round((0.5 * atk + strengthValue * 1.5) * ucYonluKesmeK));
  const ucYonluKesmeDamageMin = ucYonluKesmeSkill
    ? applyDamagePercent(ucYonluKesmeDamageFromAttack(ucYonluKesmeAttackMin), displayedSkillDamagePercent)
    : null;
  const ucYonluKesmeDamageMax = ucYonluKesmeSkill
    ? applyDamagePercent(ucYonluKesmeDamageFromAttack(ucYonluKesmeAttackMax), displayedSkillDamagePercent)
    : null;

  const defenseBeforeFish = characterDefense
    + effectiveDefenseBonus
    + (isDarkProtectionActive ? darkProtectionDefenseBonus : 0)
    + (isBuyuluZirhActive ? buyuluZirhDefenseBonus : 0);
  const defenseWithPlantFlat = defenseBeforeFish + plantDefenseFlat;
  const displayedDefenseWithFish = foodDefensePercent > 0
    ? Math.round(defenseWithPlantFlat * (100 + foodDefensePercent) / 100)
    : defenseWithPlantFlat;
  const fishDefenseBonus = displayedDefenseWithFish - defenseBeforeFish;
  const renderStatBonus = (value, { percent = false, signed = false } = {}) => {
    if (!value) return null;
    if (!signed && value <= 0) return null;
    const prefix = value > 0 ? '+' : '';
    return (
      <span className={`character-stat-bonus${value < 0 ? ' character-stat-bonus--negative' : ''}`}>
        {percent ? `${prefix}${value}%` : `${prefix}${value}`}
      </span>
    );
  };
  const renderSkillHitWithBonus = (labelNode) => (
    <div>
      {labelNode}
      {renderStatBonus(displayedSkillDamagePercent, { percent: true, signed: true })}
    </div>
  );
  const currentVillageMapImage = useMemo(() => {
    const village = villages.find((v) => Number(v.id) === Number(userCharacter?.villageId));
    if (village?.imageUrl) return normalizeSkillImageUrl(village.imageUrl);
    return '/image/villages/1koy.jpg';
  }, [villages, userCharacter?.villageId]);
  const mapPanelStyle = useMemo(
    () => ({ backgroundImage: `url('${currentVillageMapImage}')` }),
    [currentVillageMapImage],
  );
  const currentVillageBattleBg = (() => {
    const village = villages.find((v) => Number(v.id) === Number(userCharacter?.villageId));
    if (village?.battleBackgroundUrl) return normalizeSkillImageUrl(village.battleBackgroundUrl);
    return '';
  })();
  const isVillage3WaterBattle = Number(userCharacter?.villageId) === 3;

  const handleBattleBgMaxOffsetChange = (maxOffset) => {
    battleBgMaxOffsetRef.current = maxOffset;
    syncBattleBgToStep(mapWalkStepFromPos(battlePositionRef.current?.x || 0, battlePositionRef.current?.y || 0));
  };

  const mapWalkStep = mapWalkStepFromPos(positionX, positionY);

  // Arkaplan haritadaki ilerlemeye göre kayar (0. adım baş, 100. adım son).
  const syncBattleBgToStep = (step) => {
    const max = battleBgMaxOffsetRef.current;
    setBattleBgOffsetPx(max > 0 ? Math.round((max * step) / MAP_WALK_STEPS) : 0);
  };

  const handleMapWalk = (dir) => {
    const next = Math.max(0, Math.min(MAP_WALK_STEPS, mapWalkStep + dir));
    if (next === mapWalkStep || characterDeathActive) {
      stopWalkHold();
      return;
    }
    const pos = Math.round(next * MAP_WALK_UNIT);
    setPositionX(pos);
    setPositionY(pos);
    setCharacterWalkDir(dir > 0 ? 'right' : 'left');
    if (walkTimerRef.current) clearTimeout(walkTimerRef.current);
    walkTimerRef.current = setTimeout(() => {
      setCharacterWalkDir(null);
      walkTimerRef.current = null;
    }, 550);
  };

  walkFnRef.current = handleMapWalk;
  const startWalkHold = (dir) => (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    stopWalkHold();
    walkFnRef.current?.(dir);
    walkHoldRef.current = setInterval(() => walkFnRef.current?.(dir), 500);
  };
  const walkHoldProps = (dir) => ({
    onPointerDown: startWalkHold(dir),
    onPointerUp: stopWalkHold,
    onPointerLeave: stopWalkHold,
    onPointerCancel: stopWalkHold,
    onContextMenu: (e) => e.preventDefault(),
    // Klavye (Enter/Space) için tek adım; fareyle tıklama zaten basınca adım attı.
    onClick: (e) => { if (e.detail === 0) handleMapWalk(dir); },
  });

  const slotLabels = {
    weapon: getSlotLabel('weapon'),
    armor: getSlotLabel('armor'),
    helmet: getSlotLabel('helmet'),
    boots: getSlotLabel('boots'),
    shield: getSlotLabel('shield'),
    earring: getSlotLabel('earring'),
    necklace: getSlotLabel('necklace'),
    pot1: getSlotLabel('pot1'),
    pot2: getSlotLabel('pot2'),
  };
  const primaryEquipment = equippedItems.filter((item) => item.slotType !== 'pot1' && item.slotType !== 'pot2');
  const orderedEquipment = Object.keys(slotLabels).map((slotType) => ({
    slotType,
    label: slotLabels[slotType],
    item: equippedItems.find((eq) => eq.slotType === slotType),
  }));

  const buildEquipmentEffectRows = (item) => {
    if (!item) return [];
    const plating = getActivePlating(item);
    const statRows = buildDetailedStatRows(item, {
      platingBonusPercent: plating?.bonusPercent || 0
    }).map((row, index) => ({
      key: `stat-${index}`,
      text: `${row.label}: ${row.value}`,
      bonusText: row.bonusText,
      bonusClass: row.bonusClass
    }));
    const efsunRows = buildEfsunRows(
      item.efsunlar,
      getEfsunKindForSlotType(item.slotType) || 'armor'
    ).map((row, index) => ({
      key: `efsun-${index}`,
      label: row.label,
      value: row.value,
      colorClass: row.colorClass,
    }));
    return { statRows, efsunRows, plating };
  };

  const applyMonsterDamageFloats = (oldList, updatedList, monsterHits = null) => {
    const oldMap = new Map(oldList.map(m => [m.id, m]));
    const hitMap = new Map();
    if (Array.isArray(monsterHits)) {
      let anyBoth = false;
      let anyCrit = false;
      let anyPierce = false;
      monsterHits.forEach((hit) => {
        if (hit && typeof hit.monsterId === 'number') {
          hitMap.set(hit.monsterId, hit);
          if (hit.critical && hit.pierced) anyBoth = true;
          else if (hit.critical) anyCrit = true;
          else if (hit.pierced) anyPierce = true;
        }
      });
      if (anyBoth) playHitTypeSound(true, true);
      else if (anyCrit) playHitTypeSound(true, false);
      else if (anyPierce) playHitTypeSound(false, true);
    }
    const damages = {};
    const dying = [];
    const now = Date.now();
    const buildFloat = (monsterId, fallbackValue) => {
      const hitInfo = hitMap.get(monsterId);
      return {
        value: typeof hitInfo?.damage === 'number' ? hitInfo.damage : fallbackValue,
        createdAt: now,
        critical: Boolean(hitInfo?.critical),
        pierced: Boolean(hitInfo?.pierced),
      };
    };
    updatedList.forEach(m => {
      const old = oldMap.get(m.id);
      if (!old) return;
      if (hitMap.has(m.id)) {
        damages[m.id] = buildFloat(m.id, Math.max(0, old.hp - m.hp));
      } else if (old.hp < m.hp) {
        damages[m.id] = {
          value: m.hp - old.hp,
          createdAt: now,
          heal: true,
        };
      } else if (old.hp > m.hp) {
        damages[m.id] = buildFloat(m.id, old.hp - m.hp);
      }
    });
    oldList.forEach(m => {
      if (!updatedList.find(u => u.id === m.id)) {
        if (isSpawnProtected(m)) return;
        const metin = isMetinEncounter(m);
        const removeMs = deathAnimDurationMs(m.battleSprites, m);
        const side = m.originSide || m.side || 'left';
        const dir = side === 'right' ? 1 : -1;
        const deathKnockbackPx = isKnockbackImmuneMonster(m)
          ? 0
          : Math.round(knockbackAmountPx(false) * 0.85);
        const removeAt = now + removeMs;
        dying.push({
          ...m,
          damageDealt: m.hp,
          hp: 0,
          removeAt,
          anim: metin ? 'idle' : 'death',
          spawnPhase: 'ready',
          knockbackPx: dir * deathKnockbackPx,
          knockbackUntil: deathKnockbackPx ? now + removeMs : 0,
          knockbackMoveMs: 0,
          knockbackOutMs: deathKnockbackPx ? 260 : 0,
        });
        if (metin) {
          setTimeout(() => {
            playSfxFile(METIN_BREAK_SOUND, 1, 'character');
            setDyingMonsters((prev) =>
              prev.map((d) =>
                d.id === m.id && d.removeAt === removeAt
                  ? { ...d, anim: 'death' }
                  : d
              )
            );
          }, METIN_DEATH_LEAD_MS);
        }
        damages[m.id] = buildFloat(m.id, m.hp);
      }
    });
    if (Object.keys(damages).length > 0) {
      setMonsterDamageFloats(prev => ({ ...prev, ...damages }));
      Object.keys(damages).forEach(id => {
        setTimeout(() => setMonsterDamageFloats(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        }), 1600);
      });
    }
    if (dying.length > 0) {
      setDyingMonsters(prev => [...prev, ...dying]);
      dying.forEach(d => {
        const ms = d.removeAt - now;
        setTimeout(
          () => setDyingMonsters(prev => prev.filter(m => m.id !== d.id || m.removeAt !== d.removeAt)),
          ms
        );
      });
    }
  };

  const serverMonsterToInstance = (monster, side, slotIndex) => {
    const metin = isMetinEncounter(monster);
    const resolvedSide = metin ? 'center' : side;
    const resolvedSlot = metin ? 0 : slotIndex;
    return {
      id: monster.id,
      monsterId: monster.monsterId,
      name: monster.name,
      level: monster.level,
      hp: monster.hp,
      maxHp: monster.maxHp,
      attackMin: monster.attackMin,
      attackMax: monster.attackMax,
      defense: monster.defense,
      exp: monster.exp,
      goldMin: monster.goldMin,
      goldMax: monster.goldMax,
      attackSpeed: monster.attackSpeed || 1,
      attackSound: monster.attackSound || 'sounds/monsters/default_attack.mp3',
      damageType: monster.damageType || 'none',
      battleSprites: monster.battleSprites || null,
      encounterKind: monster.encounterKind || '',
      encounterConfigId: monster.encounterConfigId || 0,
      spawnedAt: monster.spawnedAt || 0,
      side: resolvedSide,
      originSide: resolvedSide,
      slotIndex: resolvedSlot,
      anim: metin ? 'idle' : 'run',
      knockbackPx: 0,
      knockbackUntil: 0,
      knockbackMoveMs: 0,
      knockbackOutMs: 0,
      spawnPhase: metin ? 'predrop' : 'approaching',
    };
  };

  const addServerMonsterInstances = (monstersData) => {
    setMonsterInstances((prevInstances) => {
      const existingIds = new Set(prevInstances.map((m) => m.id));
      const newInstances = [...prevInstances];
      monstersData.forEach((monster) => {
        if (existingIds.has(monster.id)) return;
        const metin = isMetinEncounter(monster);
        const side = metin ? 'center' : (Math.random() < 0.5 ? 'left' : 'right');
        const slotIndex = metin ? 0 : newInstances.filter((i) => i.side === side).length;
        newInstances.push(serverMonsterToInstance(monster, side, slotIndex));
        existingIds.add(monster.id);
      });
      return newInstances;
    });
  };

  const applyDungeonView = (view) => {
    if (!view) {
      setDungeon(null);
      return;
    }
    if (view.serverNow) setDungeonClockOffset(view.serverNow - Date.now());
    setDungeon(view);
    if (view.ended && view.result && view.result !== 'left') setDungeonResult(view);
  };

  const syncMonstersAfterPull = (ev) => {
    // Ölü (can 0) canavarlar sahneye geri eklenmez; yoksa ölen canavar ve yazısı ekranda kalıyordu.
    const serverMonsters = Array.isArray(ev?.monsters) ? ev.monsters.filter((m) => !(m?.hp <= 0)) : ev?.monsters;
    if (!Array.isArray(serverMonsters)) {
      const newOnes = ev?.newInstances || [];
      if (newOnes.length > 0) addServerMonsterInstances(newOnes);
      return;
    }
    battleCombatEpochRef.current += 1;
    setMonsterInstances((prev) => {
      const prevMap = new Map(prev.map((m) => [m.id, m]));
      const sideSlots = { left: 0, right: 0 };
      prev.forEach((m) => {
        if (serverMonsters.some((s) => s.id === m.id)) {
          const side = m.side || 'left';
          sideSlots[side] = Math.max(sideSlots[side], (m.slotIndex || 0) + 1);
        }
      });
      return serverMonsters.map((monster) => {
        const existing = prevMap.get(monster.id);
        if (existing) {
          return mergeMonsterBattleState([existing], [monster])[0];
        }
        const metin = isMetinEncounter(monster);
        const side = metin ? 'center' : (Math.random() < 0.5 ? 'left' : 'right');
        const slotIndex = metin ? 0 : sideSlots[side];
        if (!metin) sideSlots[side] += 1;
        return serverMonsterToInstance(monster, side, slotIndex);
      });
    });
  };

  const syncBattleSnapshot = (snap, { resetVisuals = false, refillMp = false } = {}) => {
    if (snap && Array.isArray(snap.monsters)) applyDungeonView(snap.dungeon || null);
    if (snap?.character) {
      const ch = snap.character;
      const maxHP = ch.maxHp || characterMaxHPRef.current;
      const maxMP = ch.maxMp || characterMaxMPRef.current;
      const snapHp = typeof ch.currentHp === 'number' ? ch.currentHp : maxHP;
      if (!characterDeathActiveRef.current) {
        const nextHp = snapHp > 0 ? Math.min(snapHp, maxHP) : (maxHP > 0 ? maxHP : 0);
        setCharacterHP(nextHp);
        characterHPRef.current = nextHp;
      }
      const nextMp = refillMp ? maxMP : Math.min(ch.currentMp ?? maxMP, maxMP);
      setCharacterMP(nextMp);
      characterMPRef.current = nextMp;
      if (maxHP > 0) {
        setCharacterMaxHP(maxHP);
        characterMaxHPRef.current = maxHP;
      }
      if (maxMP > 0) {
        setCharacterMaxMP(maxMP);
        characterMaxMPRef.current = maxMP;
      }
    }

    if (!Array.isArray(snap?.monsters)) return;

    if (snap.monsters.length === 0) {
      setDyingMonsters([]);
      setMonsterInstances([]);
      monsterInstancesRef.current = [];
      return;
    }

    if (resetVisuals || monsterInstancesRef.current.length === 0) {
      setMonsterInstances(snap.monsters.map((m, i) => {
        const side = i % 2 === 0 ? 'left' : 'right';
        return serverMonsterToInstance(m, side, Math.floor(i / 2));
      }));
      return;
    }

    setMonsterInstances(mergeMonsterBattleState(monsterInstancesRef.current, snap.monsters));
  };

  const ensureBattleJoinedRef = useRef(null);

  const ensureBattleJoined = useCallback(async () => {
    const socket = battleSocketRef.current;
    const char = userCharacterRef.current;
    if (!socket || !char?.villageId || !battleAuthedRef.current || characterDeathActiveRef.current) {
      return false;
    }
    if (
      battleJoinedRef.current
      && battleReadyRef.current
      && lastJoinedBattleRef.current?.villageId === char.villageId
    ) {
      return true;
    }
    if (joinInFlightRef.current) {
      return joinInFlightRef.current;
    }

    const task = (async () => {
      try {
        battleJoinedRef.current = false;
        setBattleReady(false);
        const pos = battlePositionRef.current;
        const snap = await socket.send('join', {
          villageId: char.villageId,
          step: pos.x * pos.y,
          x: pos.x,
          y: pos.y,
          refillMp: true,
          forceEquipmentReload: true,
        }, 'snapshot');
        battleJoinedRef.current = true;
        setBattleReady(true);
        setBattleReconnecting(false);
        syncBattleSnapshot(snap, { resetVisuals: true, refillMp: true });
        fetchBattleLoadout({ force: true }).catch(() => {});
        lastJoinedBattleRef.current = {
          x: pos.x,
          y: pos.y,
          villageId: char.villageId,
        };
        return true;
      } catch (err) {
        battleJoinedRef.current = false;
        setBattleReady(false);
        addDropNotification(err.message || t('notifyBattleConnectionFailed'));
        return false;
      } finally {
        if (joinInFlightRef.current === task) {
          joinInFlightRef.current = null;
        }
      }
    })();

    joinInFlightRef.current = task;
    return task;
  }, [t]);

  ensureBattleJoinedRef.current = ensureBattleJoined;

  useEffect(() => {
    userCharacterRef.current = userCharacter;
  }, [userCharacter]);

  useEffect(() => {
    battleAuthedRef.current = battleAuthed;
  }, [battleAuthed]);

  useEffect(() => {
    const char = characterCtx?.character;
    if (!char) return;
    userCharacterRef.current = char;
    setUserCharacter((prev) => (prev ? { ...prev, ...char } : char));
    if (characterHPRef.current <= 0 && !characterDeathActiveRef.current) {
      applyCharacterFromServer(char);
    }
  }, [characterCtx?.character]);

  useEffect(() => {
    if (!battleAuthed || !userCharacter?.villageId || characterDeathActive) return;
    ensureBattleJoined();
  }, [battleAuthed, userCharacter?.villageId, characterDeathActive, ensureBattleJoined]);

  const playLevelUpSound = (newLevel) => {
    if (newLevel != null && levelUpSoundPlayedForRef.current === newLevel) return;
    if (newLevel != null) levelUpSoundPlayedForRef.current = newLevel;
    playSfxFile(LEVEL_UP_SOUND, 1, 'character');
  };

  useEffect(() => {
    const level = userCharacter?.level;
    if (typeof level !== 'number') return;
    const prev = prevCharacterLevelRef.current;
    prevCharacterLevelRef.current = level;
    if (prev != null && level > prev) {
      playLevelUpSound(level);
    }
  }, [userCharacter?.level]);

  const applyExpGainLocally = (expGained) => {
    if (!expGained || expGained <= 0) return;
    const current = userCharacterRef.current;
    const toNextNow = current?.expToNextLevel || 0;
    if (current && toNextNow > 0 && toNextNow - expGained <= 0) {
      playLevelUpSound((current.level || 1) + 1);
    }
    const expFloatId = `exp-bar-gain-${Date.now()}-${Math.random()}`;
    setExpBarGainFloats((prev) => [...prev, {
      id: expFloatId,
      value: expGained,
      createdAt: Date.now(),
    }]);
    setTimeout(() => setExpBarGainFloats((prev) => prev.filter((d) => d.id !== expFloatId)), 1100);
    setUserCharacter((prev) => {
      if (!prev) return prev;
      const toNext = prev.expToNextLevel || 0;
      const newExp = (prev.exp || 0) + expGained;
      if (toNext <= 0) {
        return { ...prev, exp: newExp };
      }
      const newCurrent = (prev.currentLevelExp || 0) + expGained;
      const newToNext = toNext - expGained;
      const totalRequired = newCurrent + Math.max(newToNext, 0);
      const newProgress = totalRequired > 0
        ? Math.round((newCurrent / totalRequired) * 10000) / 100
        : 100;
      if (newToNext <= 0) {
        // Level atlama: hafif exp endpoint ile sunucu yanıtını bekle
        fetchExpProgress();
        return {
          ...prev,
          exp: newExp,
          currentLevelExp: newCurrent,
          expToNextLevel: 0,
          expProgress: newProgress,
        };
      }
      return {
        ...prev,
        exp: newExp,
        currentLevelExp: newCurrent,
        expToNextLevel: newToNext,
        expProgress: newProgress,
      };
    });
  };

  const processBattleDrops = (newItems, newGold, expGained) => {
    if ((!newItems || newItems.length === 0) && !newGold && !expGained) return;
    const itemsWithId = (newItems || []).map((item) => ({ ...item, id: `drop-${Date.now()}-${Math.random()}` }));
    setDropHistory((prev) => [...prev, { id: `evt-${Date.now()}`, gold: newGold, items: itemsWithId, exp: expGained || 0 }]);

    (newItems || []).forEach((item, index) => {
      if (item?.imageUrl) {
        spawnDropFlyAnimation(item.imageUrl, index);
      }
    });
    // Eşya düşünce telefon kısa titrer (sık düşüşlerde en fazla 0,7 sn'de bir).
    if ((newItems || []).length > 0) vibrateShort();

    // Bildirim sadece item düşünce gösterilir (EXP ve Yang için bildirim yok)
    const itemNames = (newItems || []).map((item) => `${item.itemName} x${item.count || 1}`).join(', ');
    if (itemNames) {
      addDropNotification(t('notifyGains', { gains: itemNames }), 'item');
    }

    if (expGained > 0) {
      applyExpGainLocally(expGained);
    }
  };

  const showHpGainVisual = (hpBefore, gainAmount) => {
    if (!gainAmount || gainAmount <= 0 || characterDeathActiveRef.current) return;
    const healBarId = `hp-bar-heal-${Date.now()}-${Math.random()}`;
    setHpBarHealFloats((prev) => [...prev, {
      id: healBarId,
      value: gainAmount,
      hpBefore,
      createdAt: Date.now(),
    }]);
    setTimeout(() => setHpBarHealFloats((prev) => prev.filter((d) => d.id !== healBarId)), 1100);
  };

  const showMpGainVisual = (mpBefore, gainAmount) => {
    if (!gainAmount || gainAmount <= 0) return;
    const healBarId = `mp-bar-heal-${Date.now()}-${Math.random()}`;
    setMpBarHealFloats((prev) => [...prev, {
      id: healBarId,
      value: gainAmount,
      mpBefore,
      createdAt: Date.now(),
    }]);
    setTimeout(() => setMpBarHealFloats((prev) => prev.filter((d) => d.id !== healBarId)), 1100);
  };

  const applyLifestealHeal = (lifestealHealed) => {
    if (!lifestealHealed || lifestealHealed <= 0 || characterDeathActiveRef.current) return;
    const maxHP = characterMaxHPRef.current || 0;
    const hpBefore = characterHPRef.current;
    const actualHeal = maxHP > 0
      ? Math.min(lifestealHealed, Math.max(0, maxHP - hpBefore))
      : lifestealHealed;
    if (actualHeal <= 0) return;

    setCharacterHP((prev) => {
      return maxHP > 0 ? Math.min(maxHP, prev + lifestealHealed) : prev + lifestealHealed;
    });
    showHpGainVisual(hpBefore, actualHeal);
  };

  const applySkillBuffsFromResult = (result) => {
    if (result.buffDurationSec != null && result.buffDurationSec > 0) {
      setAttackBuffBonus(typeof result.buffAttackBonus === 'number' ? result.buffAttackBonus : 0);
      setAttackBuffUntil(Date.now() + Number(result.buffDurationSec) * 1000);
      addDropNotification(t('notifyHavaKiliciActive', { attack: result.buffAttackBonus ?? 0, sec: result.buffDurationSec }));
    }
    if (result.rageDurationSec != null && result.rageDurationSec > 0) {
      setRageSpeedBuffUntil(Date.now() + Number(result.rageDurationSec) * 1000);
      setRageSpeedBuffValue(result.rageSpeedBonus ?? 0);
      addDropNotification(t('notifyOfkeActive', { speed: result.rageSpeedBonus ?? 0, sec: result.rageDurationSec }));
    }
    if (result.defenseDurationSec != null && result.defenseDurationSec > 0) {
      setDefenseBuffUntil(Date.now() + Number(result.defenseDurationSec) * 1000);
      setDefenseBuffValue(result.defenseBuffBonus ?? 0);
      addDropNotification(t('notifyGucluBedenActive', { defense: result.defenseBuffBonus ?? 0, sec: result.defenseDurationSec }));
    }
    if (result.atesHayaletiDurationSec != null && result.atesHayaletiDurationSec > 0) {
      setFireGhostUntil(Date.now() + Number(result.atesHayaletiDurationSec) * 1000);
      setFireGhostTickDamage(result.atesHayaletiTickDamage ?? 0);
      addDropNotification(t('notifyAtesHayaletiActive', { sec: result.atesHayaletiDurationSec, tick: result.atesHayaletiTickDamage ?? 0 }));
    }
    if (result.darkProtectionDurationSec != null && result.darkProtectionDurationSec > 0) {
      setDarkProtectionUntil(Date.now() + Number(result.darkProtectionDurationSec) * 1000);
      setDarkProtectionPercent(Number(result.darkProtectionPercent ?? 0));
      setDarkProtectionDefenseBonus(Number(result.darkProtectionDefenseBonus ?? 0));
      addDropNotification(t('notifyKaranlikKorumaActive', {
        percent: Math.round(Number(result.darkProtectionPercent ?? 0)),
        defense: Number(result.darkProtectionDefenseBonus ?? 0),
        sec: result.darkProtectionDurationSec,
      }));
    }
    if (result.buyuluKeskinlikDurationSec != null && result.buyuluKeskinlikDurationSec > 0) {
      setBuyuluKeskinlikUntil(Date.now() + Number(result.buyuluKeskinlikDurationSec) * 1000);
      setBuyuluKeskinlikAttackBonus(Number(result.buyuluKeskinlikAttackBonus ?? 0));
      setBuyuluKeskinlikLifestealPercent(Number(result.buyuluKeskinlikLifestealPercent ?? 0));
      addDropNotification(t('notifyBuyuluKeskinlikActive', {
        attack: Number(result.buyuluKeskinlikAttackBonus ?? 0),
        lifesteal: Number(result.buyuluKeskinlikLifestealPercent ?? 0),
        sec: result.buyuluKeskinlikDurationSec,
      }));
    }
    if (result.buyuluZirhDurationSec != null && result.buyuluZirhDurationSec > 0) {
      setBuyuluZirhUntil(Date.now() + Number(result.buyuluZirhDurationSec) * 1000);
      setBuyuluZirhDefenseBonus(Number(result.buyuluZirhDefenseBonus ?? 0));
      setBuyuluZirhReflectPercent(Number(result.buyuluZirhReflectPercent ?? 0));
      addDropNotification(t('notifyBuyuluZirhActive', {
        defense: Number(result.buyuluZirhDefenseBonus ?? 0),
        reflect: Math.round(Number(result.buyuluZirhReflectPercent ?? 0)),
        sec: result.buyuluZirhDurationSec,
      }));
    }
    if (result.dehsetDurationSec != null && result.dehsetDurationSec > 0) {
      setDehsetUntil(Date.now() + Number(result.dehsetDurationSec) * 1000);
      setDehsetReducePercent(Number(result.dehsetReducePercent ?? 0));
      addDropNotification(t('notifyDehsetActive', {
        percent: Number(result.dehsetReducePercent ?? 0).toFixed(1),
        sec: result.dehsetDurationSec,
      }));
    }
  };

  const buildMergedFromServer = (baseInstances, rawUpdated) => {
    const prevIds = new Set(baseInstances.map((m) => m.id));
    let merged = mergeMonsterBattleState(baseInstances, rawUpdated);
    const brandNew = rawUpdated.filter((m) => !prevIds.has(m.id));
    if (brandNew.length === 0) return merged;

    const sideSlots = { left: 0, right: 0 };
    merged.forEach((m) => {
      if (brandNew.some((b) => b.id === m.id)) return;
      const side = m.originSide || m.side || 'left';
      if (side !== 'center') {
        sideSlots[side] = Math.max(sideSlots[side], (m.slotIndex || 0) + 1);
      }
    });

    const brandNewMap = new Map();
    brandNew.forEach((monster) => {
      const metin = isMetinEncounter(monster);
      const side = metin ? 'center' : (Math.random() < 0.5 ? 'left' : 'right');
      const slotIndex = metin ? 0 : sideSlots[side];
      if (!metin) sideSlots[side] += 1;
      brandNewMap.set(monster.id, serverMonsterToInstance(monster, side, slotIndex));
    });

    return merged.map((m) => brandNewMap.get(m.id) || m);
  };

  const syncMonstersFromServer = (rawUpdated, {
    knockbackOnDamage = false,
    skillKnockback = false,
    skipCharacterAttack = false,
    combatEpochAtSend = null,
    monsterHits = null,
  } = {}) => {
    if (!Array.isArray(rawUpdated)) return;
    if (
      combatEpochAtSend != null
      && combatEpochAtSend !== battleCombatEpochRef.current
    ) {
      return;
    }
    const baseInstances = monsterInstancesRef.current;
    const merged = buildMergedFromServer(baseInstances, rawUpdated);
    applyMonsterDamageFloats(baseInstances, merged, monsterHits);
    const hitIds = knockbackOnDamage ? damagedMonsterIds(baseInstances, merged) : null;
    if (hitIds && hitIds.size > 0) {
      const kbAmount = knockbackAmountPx(skillKnockback);
      const mergedAfterHit = applyKnockbackToInstances(merged, kbAmount, hitIds);
      setMonsterInstances(mergedAfterHit);
      scheduleKnockbackReturn(
        maxKnockbackMoveMs(mergedAfterHit),
        knockedMonsterIds(mergedAfterHit),
        maxKnockbackOutMs(mergedAfterHit)
      );
      if (!skipCharacterAttack) playCharacterAttack();
    } else {
      setMonsterInstances((prev) => {
        const m = buildMergedFromServer(prev, rawUpdated);
        return m.map((inst) => {
          const was = prev.find((p) => p.id === inst.id);
          if (was?.knockbackUntil > Date.now() || was?.knockbackMoveMs) {
            return {
              ...inst,
              knockbackPx: was.knockbackPx || 0,
              knockbackUntil: was.knockbackUntil || 0,
              knockbackMoveMs: was.knockbackMoveMs || 0,
              knockbackOutMs: was.knockbackOutMs || 0,
              anim: was.anim === 'attack' || was.anim === 'run' ? was.anim : inst.anim,
            };
          }
          return inst;
        });
      });
    }
  };

  const applyTickPayload = (data) => {
    if (!data) return;
    if (characterDeathActiveRef.current) return;
    const currentInstances = monsterInstancesRef.current;
    const hpBefore = characterHPRef.current;
    const mpBefore = characterMPRef.current;
    const newHp = typeof data.newHp === 'number' ? data.newHp : hpBefore;
    const newMp = typeof data.newMp === 'number' ? data.newMp : mpBefore;
    const totalDamage = data.totalDamage || 0;
    const redirectedManaDamage = data.redirectedManaDamage || 0;
    const characterDied = Boolean(data.characterDead) || newHp <= 0;
    // Canavarın bize kritik / delici vuruşu
    if (data.incomingCritPierce) playHitTypeSound(true, true, 0.5);
    else if (data.incomingCritical) playHitTypeSound(true, false, 0.5);
    else if (data.incomingPierced) playHitTypeSound(false, true, 0.5);

    if (typeof data.pendingHp === 'number') {
      setPendingHpRestore(data.pendingHp);
    }
    if (typeof data.pendingMp === 'number') {
      setPendingMpRestore(data.pendingMp);
    }

    if (currentInstances.length > 0 && totalDamage > 0) {
      playMonsterAttackAnims();
    }
    if (characterDied) {
      setCharacterHP(0);
      characterHPRef.current = 0;
      setMonsterInstances([]);
      setDyingMonsters([]);
      monsterInstancesRef.current = [];
      if (!characterDeathActiveRef.current) {
        addDropNotification(t('notifyCharacterDied'));
        handleCharacterDeath();
      }
    } else if (Array.isArray(data.monsters)) {
      syncMonstersFromServer(data.monsters);
    }
    if (!characterDied) {
      const maxMP = characterMaxMPRef.current || 0;
      const clampedMp = maxMP > 0 ? Math.min(Math.max(0, newMp), maxMP) : Math.max(0, newMp);
      if (clampedMp > mpBefore) {
        showMpGainVisual(mpBefore, clampedMp - mpBefore);
      }
      setCharacterMP(clampedMp);

      const maxHP = characterMaxHPRef.current || 0;
      const clampedHp = maxHP > 0 ? Math.min(Math.max(0, newHp), maxHP) : Math.max(0, newHp);
      if (clampedHp > hpBefore) {
        showHpGainVisual(hpBefore, clampedHp - hpBefore);
      }
      setCharacterHP(clampedHp);
      characterHPRef.current = clampedHp;
    } else if (typeof newMp === 'number') {
      const maxMP = characterMaxMPRef.current || 0;
      setCharacterMP(maxMP > 0 ? Math.min(Math.max(0, newMp), maxMP) : Math.max(0, newMp));
    }
    if (redirectedManaDamage > 0) {
      const id = `char-mp-dmg-${Date.now()}-${Math.random()}`;
      setCharacterFloatingManaDamages((prev) => [...prev, { id, value: redirectedManaDamage, createdAt: Date.now() }]);
      setTimeout(() => setCharacterFloatingManaDamages((prev) => prev.filter((d) => d.id !== id)), 1600);
      const barId = `mp-bar-dmg-${Date.now()}-${Math.random()}`;
      setManaBarDamageFloats((prev) => [...prev, { id: barId, value: redirectedManaDamage, createdAt: Date.now() }]);
      setTimeout(() => setManaBarDamageFloats((prev) => prev.filter((d) => d.id !== barId)), 1100);
    }
    processBattleDrops(data.droppedItems, data.droppedGold, data.expGained);
    showExpBlocked(data?.expBlocked);
    if (totalDamage > 0) {
      const id = `char-dmg-${Date.now()}-${Math.random()}`;
      setCharacterFloatingDamages((prev) => [...prev, { id, value: totalDamage, createdAt: Date.now() }]);
      setTimeout(() => setCharacterFloatingDamages((prev) => prev.filter((d) => d.id !== id)), 1600);
    }
  };

  useEffect(() => {
    if (!username || characterPanelOnly) return;
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const socket = createBattleSocket();
    battleSocketRef.current = socket;

    socket.on('tick', applyTickPayload);
    socket.on('dungeon', (p) => {
      applyDungeonView(p?.dungeon || null);
      if (p && (Array.isArray(p.monsters) || (p.newInstances || []).length > 0)) {
        syncMonstersAfterPull(p);
      }
    });
    socket.on('error', (err) => {
      if (err?.message) addDropNotification(err.message);
    });
    socket.on('disconnected', () => {
      battleJoinedRef.current = false;
      setBattleReady(false);
      setBattleAuthed(false);
      battleAuthedRef.current = false;
      joinInFlightRef.current = null;
      setBattleReconnecting(true);
    });
    socket.on('reconnecting', () => {
      setBattleReconnecting(true);
    });

    socket.on('connected', async () => {
      const isReconnect = battleReadyRef.current || monsterInstancesRef.current.length > 0;
      try {
        if (!isReconnect) {
          battleJoinedRef.current = false;
          setBattleReady(false);
        } else {
          setBattleReconnecting(true);
        }
        await socket.send('auth', { token }, 'auth');
        setBattleAuthed(true);
        battleAuthedRef.current = true;
        setBattleReconnecting(false);

        if (isReconnect && battleVillageRef.current != null) {
          const pos = battlePositionRef.current;
          const snap = await socket.send('join', {
            villageId: battleVillageRef.current,
            step: pos.x * pos.y,
            x: pos.x,
            y: pos.y,
          }, 'snapshot');
          battleJoinedRef.current = true;
          setBattleReady(true);
          setBattleReconnecting(false);
          syncBattleSnapshot(snap, { resetVisuals: false });
          lastJoinedBattleRef.current = {
            x: pos.x,
            y: pos.y,
            villageId: battleVillageRef.current,
          };
        }
      } catch (e) {
        if (e.code === 'auth_failed') {
          addDropNotification(t('notifySessionExpired'));
          socket.close();
          setTimeout(redirectToLoginForInvalidSession, 1200);
          return;
        }
        addDropNotification(e.message || t('notifyAuthFailed'));
        if (isReconnect) {
          setBattleReconnecting(false);
        }
      }
    });

    socket.connect();

    return () => {
      socket.send('leave', {}, 'leave')
        .then((res) => {
          if (typeof res?.depotItemCount === 'number') {
            setDepotItemCount?.(res.depotItemCount);
          }
        })
        .catch(() => {});
      socket.close();
      battleSocketRef.current = null;
      battleJoinedRef.current = false;
      setBattleAuthed(false);
      setBattleReady(false);
      setBattleReconnecting(false);
    };
  }, [username, navigate]);

  useEffect(() => {
    battleReadyRef.current = battleReady;
  }, [battleReady]);

  useEffect(() => {
    battlePositionRef.current = { x: positionX, y: positionY };
    syncBattleBgToStep(mapWalkStepFromPos(positionX, positionY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionX, positionY]);

  useEffect(() => {
    battleVillageRef.current = userCharacter?.villageId ?? null;
    const prevVillage = lastJoinedBattleRef.current?.villageId;
    if (
      prevVillage != null
      && userCharacter?.villageId != null
      && prevVillage !== userCharacter.villageId
    ) {
      joinInFlightRef.current = null;
      battleJoinedRef.current = false;
      setBattleReady(false);
    }
    setBattleBgOffsetPx(0);
    setBattleBgRewinding(false);
    if (battleBgRewindTimerRef.current) {
      clearTimeout(battleBgRewindTimerRef.current);
      battleBgRewindTimerRef.current = null;
    }
  }, [userCharacter?.villageId]);

  useEffect(() => {
    const socket = battleSocketRef.current;
    if (!socket || !userCharacter || !battleAuthed || characterDeathActive) return;
    if (!battleJoinedRef.current || !battleReadyRef.current) return;
    if (joinInFlightRef.current) return;

    const last = lastJoinedBattleRef.current;
    if (last && last.villageId !== userCharacter.villageId) return;
    if (last && last.x === positionX && last.y === positionY && last.villageId === userCharacter.villageId) {
      return;
    }

    let cancelled = false;

    const syncPosition = async () => {
      try {
        const snap = await socket.send('join', {
          villageId: userCharacter.villageId,
          step: positionX * positionY,
          x: positionX,
          y: positionY,
        }, 'snapshot');
        if (cancelled) return;
        syncBattleSnapshot(snap, { resetVisuals: false });
        lastJoinedBattleRef.current = {
          x: positionX,
          y: positionY,
          villageId: userCharacter.villageId,
        };
      } catch (err) {
        if (!cancelled) {
          addDropNotification(err.message || t('notifyPositionUpdateFailed'));
        }
      }
    };

    // Oklarla art arda basışlarda sunucuya yalnızca son konum gider.
    const syncTimer = setTimeout(syncPosition, 300);
    return () => {
      cancelled = true;
      clearTimeout(syncTimer);
    };
  }, [positionX, positionY, userCharacter?.villageId, battleAuthed, characterDeathActive]);

  const activeCharacter = userCharacter || characterCtx?.character;
  const canUsePullMonsters = Boolean(
    activeCharacter?.villageId && battleAuthed && !characterDeathActive
  );

  const handleDungeonExit = async () => {
    setDungeonExitOpen(false);
    const socket = battleSocketRef.current;
    try {
      if (socket) {
        const p = await socket.send('dungeonLeave', {}, 'dungeon');
        applyDungeonView(p?.dungeon || null);
        syncMonstersAfterPull({ monsters: [] });
      }
    } catch (err) {
      addDropNotification(err.message || td('enterFailed'));
    }
    navigate('/dungeon');
  };

  // Savaş ekranından çıkış: alanda canavar varsa sor; evet denirse alan temizlenir.
  const handleBattleBack = () => {
    if (dungeonActive) {
      setDungeonExitOpen(true);
      return;
    }
    if (monsterInstancesRef.current?.length > 0) {
      setBattleExitOpen(true);
      return;
    }
    navigate('/game');
  };

  const handleBattleExit = async () => {
    setBattleExitOpen(false);
    const socket = battleSocketRef.current;
    try {
      if (socket) await socket.send('clearField', {}, 'event');
    } catch (_) {
      // çıkışı engelleme
    }
    syncMonstersAfterPull({ monsters: [] });
    navigate('/game');
  };

  const handlePullMonsters = async () => {
    if (!userCharacter || characterDeathActive) return;
    const socket = battleSocketRef.current;
    if (!socket) return;
    if (!battleAuthed) {
      socket.connect();
      addDropNotification(t('notifyBattleConnectionRetry'));
      return;
    }
    const joined = battleReady && battleJoinedRef.current
      ? true
      : await ensureBattleJoined();
    if (!joined) {
      return;
    }
    if (characterHPRef.current <= 0) {
      addDropNotification(t('pullMonstersRespawning'));
      return;
    }
    try {
      const ev = await socket.send('pullMonsters', {}, 'event');
      if (ev?.characterDead || ev?.combatEnded || ev?.action === 'reset') {
        setMonsterInstances([]);
        setDyingMonsters([]);
        monsterInstancesRef.current = [];
        if (ev?.characterDead && characterHPRef.current > 0) {
          setCharacterHP(0);
          characterHPRef.current = 0;
          addDropNotification(t('notifyCharacterDied'));
          handleCharacterDeath();
        }
        return;
      }
      if (ev?.action === 'encounterOffer' && ev?.encounter) {
        setEncounterOffer(ev.encounter);
        setEncounterModalOpen(true);
        return;
      }
      const newOnes = ev?.newInstances || [];
      if (newOnes.length > 0 || Array.isArray(ev?.monsters)) {
        syncMonstersAfterPull(ev);
      }
    } catch (err) {
      addDropNotification(err.message || t('notifyPullMonstersFailed'));
    }
  };

  // Savaş ekranındayken (zindan hariç) her 5 sn'de bir bulunulan yerden tek canavar gelir.
  // Alanda 3 canlı canavar varken, boss/metin varken, teklif açıkken, ölüyken veya sekme gizliyken beklenir;
  // sunucu da aynı kuralları uygular.
  const autoPullRef = useRef(null);
  autoPullRef.current = async () => {
    const socket = battleSocketRef.current;
    if (!socket || !battleJoinedRef.current) return;
    const ev = await socket.send('autoSpawn', {}, 'event');
    // Yalnızca yeni gelen canavar eklenir; tüm listeyi yeniden kurmak az önce ölen canavarı geri getirebiliyordu.
    const newOnes = ev?.newInstances || [];
    if (newOnes.length > 0) addServerMonsterInstances(newOnes);
  };
  const autoPullBusyRef = useRef(false);
  const autoPullEnabled = Boolean(
    !characterPanelOnly && battleAuthed && battleReady && !dungeonActive && !characterDeathActive && !encounterOffer
  );
  useEffect(() => {
    if (!autoPullEnabled) return undefined;
    const id = setInterval(async () => {
      if (autoPullBusyRef.current || document.hidden || characterHPRef.current <= 0) return;
      const liveMonsters = (monsterInstancesRef.current || []).filter((m) => m && m.hp > 0);
      if (liveMonsters.length >= AUTO_PULL_MAX_FIELD) return;
      if (liveMonsters.some((m) => m.encounterKind === 'boss' || m.encounterKind === 'metin')) return;
      autoPullBusyRef.current = true;
      try {
        await autoPullRef.current?.();
      } catch (_) {
        // otomatik canavar gelmezse sessizce geç
      } finally {
        autoPullBusyRef.current = false;
      }
    }, AUTO_PULL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoPullEnabled]);

  const handleAcceptEncounter = async () => {
    const socket = battleSocketRef.current;
    if (!socket || !encounterOffer) return;
    try {
      const ev = await socket.send('acceptEncounter', {}, 'event');
      setEncounterModalOpen(false);
      setEncounterOffer(null);
      const newOnes = ev?.newInstances || [];
      if (newOnes.length > 0 || Array.isArray(ev?.monsters)) {
        syncMonstersAfterPull(ev);
      }
    } catch (err) {
      addDropNotification(err.message || t('encounterAcceptFailed'));
    }
  };

  const handleDeclineEncounter = async () => {
    const socket = battleSocketRef.current;
    if (!socket) return;
    try {
      await socket.send('declineEncounter', {}, 'event');
      setEncounterModalOpen(false);
      setEncounterOffer(null);
    } catch (err) {
      addDropNotification(err.message || t('notifyOperationFailed'));
    }
  };

  const handleNormalAttack = async () => {
    if (monsterInstances.length === 0 || !userCharacter || characterHP <= 0) return;
    if (normalAttackCooldownUntil > Date.now()) return;
    if (originalGameName(equippedWeapon?.name) === 'Olta') {
      addDropNotification(t('notifyRodAttackBlocked'));
      return;
    }
    if (originalGameName(equippedWeapon?.name) === 'Kazma') {
      addDropNotification(t('notifyPickaxeAttackBlocked'));
      return;
    }
    if (originalGameName(equippedWeapon?.name) === 'Kürek') {
      addDropNotification(t('notifyShovelAttackBlocked'));
      return;
    }
    const socket = battleSocketRef.current;
    if (!socket) return;

    playSfxFile(NORMAL_ATTACK_SOUND, NORMAL_ATTACK_VOLUME, 'character');
    setNormalAttackCooldownUntil(Date.now() + estimateNormalAttackCooldownSec() * 1000);
    applyOptimisticMeleeFeedback();

    const combatEpochAtSend = battleCombatEpochRef.current;
    try {
      const ev = await socket.send('attack', {}, 'event');
      const result = ev?.result || {};
      const cooldownSec = result.normalAttackCooldown ?? estimateNormalAttackCooldownSec();
      setNormalAttackCooldownUntil(Date.now() + cooldownSec * 1000);
      applyLifestealHeal(ev?.lifestealHealed || 0);
      syncMonstersFromServer(ev?.monsters || [], {
        knockbackOnDamage: true,
        skipCharacterAttack: true,
        combatEpochAtSend,
        monsterHits: ev?.monsterHits,
      });
      processBattleDrops(ev?.droppedItems, ev?.droppedGold, ev?.expGained);
      showExpBlocked(ev?.expBlocked);
      if (ev?.newInstances?.length > 0) addServerMonsterInstances(ev.newInstances);
    } catch (err) {
      addDropNotification(err.message || t('notifyNormalAttackFailed'));
    }
  };

  const removeSkillVisualEffect = useCallback((skillId) => {
    setSkillVisualEffects((prev) => {
      if (!prev[skillId]) return prev;
      const next = { ...prev };
      delete next[skillId];
      return next;
    });
  }, []);

  const triggerSkillVisualEffect = (skill) => {
    const sheet = buildSkillEffectSheet(skill);
    if (!sheet) return;
    const persistent = isPersistentSkillEffect(skill);
    setSkillVisualEffects((prev) => ({
      ...prev,
      [skill.id]: {
        sheet,
        loop: persistent,
        untilMs: 0,
        key: Date.now(),
      },
    }));
  };

  const handleUseSkill = async (skillId) => {
    if (!userCharacter) return;
    if (skillInFlightRef.current) return;
    if (skillCooldowns[skillId] && skillCooldowns[skillId] > Date.now()) return;

    const usedSkill = skills.find((s) => s.id === skillId);
    if (usedSkill?.isBuffSkill && !usedSkill.isOfke && !usedSkill.isDefenseBuff && attackBuffUntil > Date.now()) return;
    if (usedSkill?.isOfke && rageSpeedBuffUntil > Date.now()) return;
    if (usedSkill?.isDefenseBuff && defenseBuffUntil > Date.now()) return;
    if (usedSkill?.isAtesHayaleti && fireGhostUntil > Date.now()) return;
    if (usedSkill?.isKaranlikKoruma && darkProtectionUntil > Date.now()) return;
    if (usedSkill?.isBuyuluKeskinlik && buyuluKeskinlikUntil > Date.now()) return;
    if (usedSkill?.isBuyuluZirh && buyuluZirhUntil > Date.now()) return;
    if (usedSkill?.isDehset && dehsetUntil > Date.now()) return;

    const socket = battleSocketRef.current;
    if (!socket) return;

    const isSelfBuffSkill = usedSkill?.isBuffSkill || usedSkill?.isOfke || usedSkill?.isDefenseBuff
      || usedSkill?.isAtesHayaleti || usedSkill?.isKaranlikKoruma || usedSkill?.isBuyuluKeskinlik || usedSkill?.isBuyuluZirh || usedSkill?.isDehset;

    const skillSound = getSkillUseSound(usedSkill);
    if (skillSound) playSfxFile(skillSound, 1, 'character');
    triggerSkillVisualEffect(usedSkill);
    if (!isSelfBuffSkill && monsterInstances.length > 0) {
      applyOptimisticMeleeFeedback();
    }
    const optimisticCooldownUntil = usedSkill?.cooldown
      ? Date.now() + usedSkill.cooldown * 1000
      : 0;
    if (optimisticCooldownUntil > 0) {
      setSkillCooldowns((prev) => ({ ...prev, [skillId]: optimisticCooldownUntil }));
    }

    skillInFlightRef.current = true;
    const combatEpochAtSend = battleCombatEpochRef.current;
    try {
      const ev = await socket.send('skill', { skillId }, 'event');
      const result = ev?.result || {};

      setCharacterMP((prev) => Math.max(0, prev - (result.requiredMana || 0)));
      applyLifestealHeal(ev?.lifestealHealed || 0);

      if (result.cooldown) {
        setSkillCooldowns((prev) => ({
          ...prev,
          [skillId]: Date.now() + result.cooldown * 1000,
        }));
      }
      applySkillBuffsFromResult(result);

      if (isPersistentSkillEffect(usedSkill)) {
        const untilMs = getPersistentEffectUntilMs(usedSkill, result);
        if (untilMs > Date.now()) {
          setSkillVisualEffects((prev) => {
            const current = prev[skillId];
            if (!current) return prev;
            return {
              ...prev,
              [skillId]: { ...current, untilMs },
            };
          });
        } else {
          removeSkillVisualEffect(skillId);
        }
      }

      syncMonstersFromServer(ev?.monsters || [], {
        knockbackOnDamage: (result.damage || 0) > 0,
        skillKnockback: true,
        skipCharacterAttack: !isSelfBuffSkill && monsterInstances.length > 0,
        combatEpochAtSend,
        monsterHits: ev?.monsterHits,
      });
      processBattleDrops(ev?.droppedItems, ev?.droppedGold, ev?.expGained);
      showExpBlocked(ev?.expBlocked);
      if (ev?.newInstances?.length > 0) addServerMonsterInstances(ev.newInstances);
    } catch (err) {
      removeSkillVisualEffect(skillId);
      if (optimisticCooldownUntil > 0) {
        setSkillCooldowns((prev) => {
          const next = { ...prev };
          delete next[skillId];
          return next;
        });
      }
      const msg = err.message || t('notifySkillFailed');
      addDropNotification(msg);
      if (msg.includes('zaten aktif')) {
        await fetchCharacterData({ includeLoadout: false, syncCombat: true });
      }
    } finally {
      skillInFlightRef.current = false;
    }
  };

  // Slot envanterdeki yedek yığınla dolunca görsel/isim kaybolmasın diye son bilinen görünüm.
  const potVisualRef = useRef({});
  pots.forEach((p) => {
    if (p?.slotType && p.imageUrl) potVisualRef.current[p.slotType] = { imageUrl: p.imageUrl, name: p.name };
  });

  const handleUsePot = async (potSlot) => {
    const pot = pots.find((p) => p.slotType === potSlot);
    // Sayı 0 olsa da gönder: sunucu envanterdeki aynı pottan slotu doldurur.
    if (!pot) return;
    const socket = battleSocketRef.current;
    if (!socket) return;

    try {
      const ev = await socket.send('usePot', { slot: potSlot }, 'event');
      setPendingHpRestore(ev?.pendingHp || 0);
      setPendingMpRestore(ev?.pendingMp || 0);
      if (Array.isArray(ev?.potSlots) && ev.potSlots.length > 0) {
        const bySlot = new Map(ev.potSlots.map((v) => [v.slot, v]));
        setPots((prevPots) =>
          prevPots.map((p) => {
            const v = bySlot.get(p.slotType);
            if (!v) return p;
            // Slot bitip envanterdeki aynı pottan doldurulduysa yeni yığının id'si gelir
            const visual = potVisualRef.current[p.slotType] || {};
            return {
              ...p,
              count: v.count || 0,
              itemId: v.userPotRowId || p.itemId,
              imageUrl: p.imageUrl || visual.imageUrl,
              name: p.name || visual.name,
            };
          })
        );
      } else if (typeof ev?.pot1Count === 'number' || typeof ev?.pot2Count === 'number') {
        setPots((prevPots) =>
          prevPots.map((p) => {
            if (p.slotType === 'pot1' && typeof ev.pot1Count === 'number') {
              return { ...p, count: ev.pot1Count };
            }
            if (p.slotType === 'pot2' && typeof ev.pot2Count === 'number') {
              return { ...p, count: ev.pot2Count };
            }
            return p;
          })
        );
      } else {
        setPots((prevPots) =>
          prevPots.map((p) =>
            p.slotType === potSlot ? { ...p, count: Math.max(0, p.count - 1) } : p
          )
        );
      }
    } catch (err) {
      addDropNotification(err.message || t('notifyPotFailed'));
    }
  };

  // Aynı id hem canlı hem ölen listede olmasın (ikisi de aynı ekran öğesini kullanır).
  // Savaş alanı (BattleArena) memo'lu: ekrandaki sayaçlar değişince 30 canavar baştan çizilmesin
  // diye ona giden diziler ve fonksiyonlar sabit tutulur.
  const arenaMonsters = useMemo(() => [
    ...monsterInstances,
    ...dyingMonsters.filter((d) => !monsterInstances.some((m) => m.id === d.id)),
  ], [monsterInstances, dyingMonsters]);
  const arenaHpFloats = useMemo(() => [
    ...hpBarHealFloats.map((d) => ({ ...d, heal: true })),
    ...characterFloatingDamages.map((d) => ({ ...d, heal: false })),
  ], [hpBarHealFloats, characterFloatingDamages]);
  const arenaMpFloats = useMemo(() => [
    ...mpBarHealFloats.map((d) => ({ ...d, heal: true })),
    ...manaBarDamageFloats.map((d) => ({ ...d, heal: false })),
  ], [mpBarHealFloats, manaBarDamageFloats]);
  const approachCompleteRef = useRef(null);
  approachCompleteRef.current = handleApproachComplete;
  const stableApproachComplete = useCallback((id) => approachCompleteRef.current?.(id), []);
  const deathCompleteRef = useRef(null);
  deathCompleteRef.current = handleMonsterDeathComplete;
  const stableDeathComplete = useCallback((m) => deathCompleteRef.current?.(m), []);

  // Ölüm animasyonunda can barı her zaman 0 gösterilir (eksik hasar senkronu önlenir)
  const displayCharacterHP = characterDeathActive ? 0 : Math.max(0, characterHP);

  const totalPendingHP = pendingHpRestore || 0;
  const totalPendingMP = pendingMpRestore || 0;

  const latestDropText = React.useMemo(() => {
    if (dropHistory.length === 0) return '';
    const last = dropHistory[dropHistory.length - 1];
    const parts = [];
    if (last.gold > 0) parts.push(`${last.gold} Yang`);
    if (last.items?.length > 0) {
      last.items.forEach(it => parts.push(`${it.itemName} x${it.count || 1}`));
    }
    if (last.exp > 0) parts.push(`${last.exp} EXP`);
    return parts.join(' • ');
  }, [dropHistory]);

  // Video bonusları sunucudan okunur: açılışta, dakikada bir ve video izlenince.
  useEffect(() => {
    if (characterPanelOnly) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get('/api/ads/status');
        if (!alive || !res.data?.success) return;
        setAdBoosts({
          exp: Number(res.data.adExpBoostUntil || 0) * 1000,
          drop: Number(res.data.adDropBoostUntil || 0) * 1000,
        });
      } catch (_) {
        // bonus bilgisi gelmezse göstermeyiz
      }
    };
    load();
    const id = setInterval(load, 60000);
    window.addEventListener('text2-ads-changed', load);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener('text2-ads-changed', load);
    };
  }, [characterPanelOnly]);
  const anyAdBoostActive = adBoosts.exp > Date.now() || adBoosts.drop > Date.now();
  useEffect(() => {
    if (!anyAdBoostActive) return undefined;
    const id = setInterval(() => setAdBoostNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anyAdBoostActive]);
  const adExpLeftSec = Math.max(0, Math.ceil((adBoosts.exp - adBoostNow) / 1000));
  const adDropLeftSec = Math.max(0, Math.ceil((adBoosts.drop - adBoostNow) / 1000));
  const activeAdBuffs = [
    adExpLeftSec > 0 && {
      key: 'ad-exp',
      shortLabel: tl(`🎬 Video EXP +100%: ${formatMarketBuffRemaining(adExpLeftSec)}`, `🎬 Video EXP +100%: ${formatMarketBuffRemaining(adExpLeftSec)}`),
      title: tl('Video Tecrübe Bonusu', 'Video EXP Bonus'),
      effect: tl('Video izleyerek kazandın. Canavarlardan kazanılan tecrübe %100 artar; diğer tecrübe bonuslarının üstüne biner.', 'Earned by watching a video. EXP from monsters +100%, stacks with other EXP bonuses.'),
      remaining: formatMarketBuffRemaining(adExpLeftSec),
    },
    adDropLeftSec > 0 && {
      key: 'ad-drop',
      shortLabel: tl(`🎬 Video Drop +100%: ${formatMarketBuffRemaining(adDropLeftSec)}`, `🎬 Video Drop +100%: ${formatMarketBuffRemaining(adDropLeftSec)}`),
      title: tl('Video Düşürme Bonusu', 'Video Drop Bonus'),
      effect: tl('Video izleyerek kazandın. Eşya düşürme oranı %100 artar; diğer düşürme bonuslarının üstüne biner.', 'Earned by watching a video. Item drop rate +100%, stacks with other drop bonuses.'),
      remaining: formatMarketBuffRemaining(adDropLeftSec),
    },
  ].filter(Boolean);

  const activeMarketBuffs = [
    expBoostRemainingSec > 0 && {
      key: 'exp',
      shortLabel: t('marketBuffExpShort', { time: formatMarketBuffRemaining(expBoostRemainingSec) }),
      title: t('marketBuffExpTitle'),
      effect: t('marketBuffExpEffect'),
      remaining: formatMarketBuffRemaining(expBoostRemainingSec),
    },
    dropBoostRemainingSec > 0 && {
      key: 'drop',
      shortLabel: t('marketBuffDropShort', { time: formatMarketBuffRemaining(dropBoostRemainingSec) }),
      title: t('marketBuffDropTitle'),
      effect: t('marketBuffDropEffect'),
      remaining: formatMarketBuffRemaining(dropBoostRemainingSec),
    },
    lootExpBoostRemainingSec > 0 && {
      key: 'loot-exp',
      shortLabel: t('lootBuffExpShort', { time: formatMarketBuffRemaining(lootExpBoostRemainingSec) }),
      title: t('lootBuffExpTitle'),
      effect: t('lootBuffExpEffect'),
      remaining: formatMarketBuffRemaining(lootExpBoostRemainingSec),
    },
    lootDropBoostRemainingSec > 0 && {
      key: 'loot-drop',
      shortLabel: t('lootBuffDropShort', { time: formatMarketBuffRemaining(lootDropBoostRemainingSec) }),
      title: t('lootBuffDropTitle'),
      effect: t('lootBuffDropEffect'),
      remaining: formatMarketBuffRemaining(lootDropBoostRemainingSec),
    },
  ].filter(Boolean);

  const activeBattleBuffs = useMemo(() => {
    const buffs = [];
    if (buffRemainingSec > 0) {
      buffs.push({
        key: 'attack',
        label: t('buffAttack', { bonus: attackBuffBonus, sec: buffRemainingSec }),
        variant: 'default',
      });
    }
    if (rageBuffRemainingSec > 0) {
      buffs.push({
        key: 'rage',
        label: t('buffRage', { speed: rageSpeedBuffValue, sec: rageBuffRemainingSec }),
        variant: 'rage',
      });
    }
    if (fireGhostRemainingSec > 0) {
      buffs.push({
        key: 'fireGhost',
        label: t('buffFireGhost', { damage: fireGhostTickDamage, sec: fireGhostRemainingSec }),
        variant: 'rage',
      });
    }
    if (darkProtectionRemainingSec > 0) {
      buffs.push({
        key: 'darkProtection',
        label: t('buffDarkProtection', {
          percent: Math.round(darkProtectionPercent),
          defense: darkProtectionDefenseBonus,
          sec: darkProtectionRemainingSec,
        }),
        variant: 'default',
      });
    }
    if (buyuluKeskinlikRemainingSec > 0) {
      buffs.push({
        key: 'buyuluKeskinlik',
        label: t('buffBuyuluKeskinlik', {
          attack: buyuluKeskinlikAttackBonus,
          lifesteal: Math.round(buyuluKeskinlikLifestealPercent),
          sec: buyuluKeskinlikRemainingSec,
        }),
        variant: 'rage',
      });
    }
    if (buyuluZirhRemainingSec > 0) {
      buffs.push({
        key: 'buyuluZirh',
        label: t('buffBuyuluZirh', {
          defense: buyuluZirhDefenseBonus,
          reflect: Math.round(buyuluZirhReflectPercent),
          sec: buyuluZirhRemainingSec,
        }),
        variant: 'default',
      });
    }
    if (dehsetRemainingSec > 0) {
      buffs.push({
        key: 'dehset',
        label: t('buffDehset', {
          percent: Number(dehsetReducePercent).toFixed(1),
          sec: dehsetRemainingSec,
        }),
        variant: 'default',
      });
    }
    activeFishFoodBuffs.forEach((buff) => {
      buffs.push({
        key: `fish-${buff.speciesId || buff.speciesName}`,
        label: t('buffFishFood', {
          name: buff.speciesName,
          effect: buff.effectSummary,
          sec: buff.remainingSec,
        }),
        variant: 'fish',
      });
    });
    activePlantFoodBuffs.forEach((buff) => {
      buffs.push({
        key: `plant-${buff.cropId || buff.cropName}`,
        label: t('buffPlantFood', {
          name: buff.cropName,
          effect: buff.effectSummary,
          sec: buff.remainingSec,
        }),
        variant: 'fish',
      });
    });
    return buffs;
  }, [
    buffRemainingSec,
    attackBuffBonus,
    rageBuffRemainingSec,
    rageSpeedBuffValue,
    fireGhostRemainingSec,
    fireGhostTickDamage,
    darkProtectionRemainingSec,
    darkProtectionPercent,
    darkProtectionDefenseBonus,
    buyuluKeskinlikRemainingSec,
    buyuluKeskinlikAttackBonus,
    buyuluKeskinlikLifestealPercent,
    buyuluZirhRemainingSec,
    buyuluZirhDefenseBonus,
    buyuluZirhReflectPercent,
    dehsetRemainingSec,
    dehsetReducePercent,
    activeFishFoodBuffs,
    activePlantFoodBuffs,
    t,
  ]);

  if (loading) {
    return (
      <div className="auth-container auth-container--selection battle-page">
        <div className="auth-loading">{tc('loading')}</div>
      </div>
    );
  }

  // Savaş ekranı ve arena aynı karakter panelini kullanır. Sadece görünürken hesaplanır
  // (her çizimde tüm ekipman satırlarını kurmak savaş ekranını yavaşlatıyordu).
  const renderCharacterPanel = () => (
            <div className="character-tab-panel">
              <div className="character-summary-grid">
                <div className="character-summary-card">
                  <span className="summary-label">{t('characterTotalHp')}</span>
                  <strong>
                    {characterMaxHP}
                    {renderStatBonus(foodMaxHpFlat)}
                  </strong>
                </div>
                <div className="character-summary-card">
                  <span className="summary-label">{t('characterTotalMp')}</span>
                  <strong>{characterMaxMP}</strong>
                </div>
                <div className="character-summary-card">
                  <span className="summary-label">
                    {isBuffActive ? t('characterNormalAttackBuff') : t('characterNormalAttackEstimate')}
                  </span>
                  <strong>
                    {displayedNormalAttackMinWithFish} - {displayedNormalAttackMaxWithFish}
                    {renderStatBonus(displayedNormalDamagePercent, { percent: true, signed: true })}
                  </strong>
                </div>
                <div className="character-summary-card">
                  <span className="summary-label">
                    {skillDefenseBonus > 0 || fishDefenseBonus > 0
                      ? t('characterTotalDefenseBuff')
                      : t('characterTotalDefense')}
                  </span>
                  <strong>
                    {displayedDefenseWithFish}
                    {renderStatBonus(skillDefenseBonus)}
                    {renderStatBonus(fishDefenseBonus)}
                  </strong>
                </div>
                <div className="character-summary-card">
                  <span className="summary-label">{t('characterNormalAttackSpeed')}</span>
                  <strong>
                    {t('characterNormalAttackSpeedValue', { sec: displayedNormalAttackCooldown })}
                    {renderStatBonus(equipmentAttackSpeedPercent + foodAttackSpeedPercent, { percent: true })}
                    {rageSpeedBonusActive && renderStatBonus(rageSpeedBuffValue, { percent: true })}
                  </strong>
                </div>
                <div className="character-summary-card">
                  <span className="summary-label">{t('characterCriticalChance')}</span>
                  <strong>
                    {t('characterPercentStat', { value: displayedCriticalPercent })}
                    {renderStatBonus(efsunCriticalPercent, { percent: true })}
                    {renderStatBonus(foodCriticalPercent, { percent: true })}
                  </strong>
                </div>
                <div className="character-summary-card">
                  <span className="summary-label">{t('characterPierceChance')}</span>
                  <strong>
                    {t('characterPercentStat', { value: displayedPiercePercent })}
                    {renderStatBonus(efsunPiercePercent, { percent: true })}
                    {renderStatBonus(foodPiercePercent, { percent: true })}
                  </strong>
                </div>
                {foodHpProductionPercent > 0 && (
                  <div className="character-summary-card">
                    <span className="summary-label">{t('characterHpRegen')}</span>
                    <strong>
                      {renderStatBonus(foodHpProductionPercent, { percent: true })}
                    </strong>
                  </div>
                )}
                {foodExperiencePercent > 0 && (
                  <div className="character-summary-card">
                    <span className="summary-label">{t('characterExpBonus')}</span>
                    <strong>
                      {renderStatBonus(foodExperiencePercent, { percent: true })}
                    </strong>
                  </div>
                )}
              </div>

              <div className="character-section-block">
                <h3>{t('characterFishFoodTitle')}</h3>
                {activeFishFoodBuffs.length === 0 ? (
                  <p>{t('characterFishFoodNone')}</p>
                ) : (
                  <div className="character-efsun-list">
                    {activeFishFoodBuffs.map((buff) => (
                      <div key={`fish-food-${buff.speciesId}`} className="character-fish-food-row">
                        <span className="character-fish-food-row__name">{buff.speciesName}</span>
                        <span className="character-fish-food-row__time">
                          {t('characterFishFoodRemaining', { sec: buff.remainingSec })}
                        </span>
                        <span className="character-fish-food-row__effect">{buff.effectSummary}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="character-section-block">
                <h3>{t('characterPlantFoodTitle')}</h3>
                {activePlantFoodBuffs.length === 0 ? (
                  <p>{t('characterPlantFoodNone')}</p>
                ) : (
                  <div className="character-efsun-list">
                    {activePlantFoodBuffs.map((buff) => (
                      <div key={`plant-food-${buff.cropId}`} className="character-fish-food-row">
                        <span className="character-fish-food-row__name">{buff.cropName}</span>
                        <span className="character-fish-food-row__time">
                          {t('characterPlantFoodRemaining', { sec: buff.remainingSec })}
                        </span>
                        <span className="character-fish-food-row__effect">{buff.effectSummary}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="character-section-block">
                <h3>{t('characterEfsunTitle')}</h3>
                {equippedEfsunTotals.length === 0 ? (
                  <p>{t('characterEfsunNone')}</p>
                ) : (
                  <div className="character-efsun-list">
                    {equippedEfsunTotals.map((efsun) => (
                      <div key={efsun.type} className="character-efsun-row">
                        <span>{efsun.label}</span>
                        <strong className={getEfsunValueColorClass(efsun.type, efsun.value)}>
                          {formatEfsunValue(efsun.type, efsun.value)}
                        </strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="character-section-block">
                <h3>{t('characterSkillsTitle')}</h3>
                {skills.length === 0 ? (
                  <p>{t('characterSkillsNone')}</p>
                ) : (
                  <div className="character-skill-grid">
                    {skills.map((skill) => (
                      <div key={`character-skill-${skill.id}`} className="character-skill-card">
                        <div className="character-skill-title">
                          <span>{skill.skillName}</span>
                          {typeof skill.level === 'number' && (
                            <span>{` (${formatSkillLevelForDevelopment(skill.level)})`}</span>
                          )}
                          {skill.imageUrl && !brokenSkillImages[skill.id] ? (
                            <img
                              className="character-skill-icon"
                              src={normalizeSkillImageUrl(skill.imageUrl)}
                              alt={skill.skillName || 'Skill'}
                              onError={() => setBrokenSkillImages(prev => ({ ...prev, [skill.id]: true }))}
                            />
                          ) : (
                            <span className="character-skill-icon-fallback">{getSkillImageLabel(skill.imageUrl)}</span>
                          )}
                        </div>
                        {skill.isBuffSkill ? (
                          <div>
                            {t('skillGivesAttackBonus', { value: isBuffActive ? attackBuffBonus : previewBuffAttackBonus, active: skillActiveSuffix(isBuffActive) })}
                          </div>
                        ) : skill.isKilicCevirme ? (
                          <>
                            {renderSkillHitWithBonus(t('skillPossibleHit', { min: kilicCevirmeDamageMin, max: kilicCevirmeDamageMax }))}
                            <div>{t('skillTargetMax', { count: 12 })}</div>
                            <div>{t('skillMana', { value: 30 })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 15 })}</div>
                          </>
                        ) : skill.isHamle ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(hamleDamageMin, hamleDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 4 })}</div>
                            <div>{t('skillMana', { value: 25 })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 12 })}</div>
                          </>
                        ) : skill.isUcYonluKesme ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(ucYonluKesmeDamageMin, ucYonluKesmeDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 5 })}</div>
                            <div>{t('skillMana', { value: 25 })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 12 })}</div>
                          </>
                        ) : skill.isOfke ? (
                          <>
                            <div>{t('skillGivesSpeed', { value: rageSpeedBuffUntil > Date.now() ? rageSpeedBuffValue : ofkeSpeedBonus, active: skillActiveSuffix(rageSpeedBuffUntil > Date.now()) })}</div>
                            <div>{t('skillMana', { value: ofkeMana })}</div>
                            <div>{t('skillDurationSec', { sec: ofkeDuration })}</div>
                            <div>{t('skillCooldownSec', { sec: ofkeDuration })}</div>
                          </>
                        ) : skill.isDefenseBuff ? (
                          <>
                            <div>{t('skillGivesDefense', { value: isDefenseBuffActive ? defenseBuffValue : previewDefenseBonus, active: skillActiveSuffix(isDefenseBuffActive) })}</div>
                            <div>{t('skillMana', { value: defenseBuffMana })}</div>
                            <div>{t('skillDurationSec', { sec: defenseBuffDuration })}</div>
                            <div>{t('skillCooldownSec', { sec: defenseBuffDuration })}</div>
                          </>
                        ) : skill.isGucluVurus ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.gucluVurusDamageMin, skill.gucluVurusDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 10 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '57-225' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 25 })}</div>
                          </>
                        ) : skill.isSiddetliVurus ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.siddetliVurusDamageMin, skill.siddetliVurusDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 7 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '67-247' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 15 })}</div>
                          </>
                        ) : skill.isRuhVurusu ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.ruhVurusuDamageMin, skill.ruhVurusuDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 4 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '66-210' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 15 })}</div>
                          </>
                        ) : skill.isKilicDarbesi ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.kilicDarbesiDamageMin, skill.kilicDarbesiDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 10 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '46-190' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 15 })}</div>
                          </>
                        ) : skill.isHayaletVurus ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.hayaletVurusDamageMin, skill.hayaletVurusDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 9 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '46-190' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 12 })}</div>
                          </>
                        ) : skill.isAlevVurusu ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.alevVurusuDamageMin, skill.alevVurusuDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 15 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '67-235' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 12 })}</div>
                          </>
                        ) : skill.isKaranlikVurus ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.karanlikVurusDamageMin, skill.karanlikVurusDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 5 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '37-205' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 7 })}</div>
                          </>
                        ) : skill.isAtesHayaleti ? (
                          <>
                            {renderSkillHitWithBonus(t('skillTickHit', { range: skillRangeText(skill.atesHayaletiDamageMin, skill.atesHayaletiDamageMax) }))}
                            <div>{t('skillTargetMaxInterval', { count: 1, interval: 3 })}</div>
                            <div>{t('skillInitialMana', { value: fireGhostInitialMana })}</div>
                            <div>{t('skillDurationSec', { sec: fireGhostDuration })}</div>
                            <div>{t('skillManaPerSec', { value: fireGhostTickMana })}</div>
                          </>
                        ) : skill.isKaranlikKoruma ? (
                          <>
                            <div>{t('skillBaseRate', { value: Math.round(Number(skill.karanlikKorumaPercent || darkProtectionPercent || 0)) })}</div>
                            <div>{t('skillExtraDefense', { value: isDarkProtectionActive ? darkProtectionDefenseBonus : previewDarkProtectionDefenseBonus })}</div>
                            <div>{t('skillInitialMana', { value: darkProtectionInitialMana })}</div>
                            <div>{t('skillDurationSec', { sec: darkProtectionDuration })}</div>
                            <div>{t('skillManaPerSec', { value: darkProtectionTickMana })}</div>
                          </>
                        ) : skill.isBuyuCozme ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.buyuCozmeDamageMin, skill.buyuCozmeDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 4 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '37-205' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 10 })}</div>
                          </>
                        ) : skill.isParmakDarbesi ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.parmakDarbesiDamageMin, skill.parmakDarbesiDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 4 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '37-205' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 10 })}</div>
                          </>
                        ) : skill.isEjderhaDonusu ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.ejderhaDonusuDamageMin, skill.ejderhaDonusuDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 12 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '57-237' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 15 })}</div>
                          </>
                        ) : skill.isDehset ? (
                          <>
                            <div>{t('skillReduceFollowHit', { value: (isDehsetActive ? dehsetReducePercent : (skill.dehsetReducePercent ?? 0)).toFixed(1) })}{skillActiveSuffix(isDehsetActive)}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '66-210' })}</div>
                            <div>{t('skillDurationSec', { sec: skill.dehsetDurationSec ?? 65 })}</div>
                            <div>{t('skillCooldownSec', { sec: skill.dehsetCooldownSec ?? 35 })}</div>
                          </>
                        ) : skill.isKaranlikTas ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.karanlikTasDamageMin, skill.karanlikTasDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 1 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '91-355' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 24 })}</div>
                          </>
                        ) : skill.isBuyuluZirh ? (
                          <>
                            <div>{t('skillGivesDefense', { value: isBuyuluZirhActive ? buyuluZirhDefenseBonus : buyuluZirhPreviewDefenseBonus, active: skillActiveSuffix(isBuyuluZirhActive) })}</div>
                            <div>{t('skillReflect', { value: Math.round(isBuyuluZirhActive ? buyuluZirhReflectPercent : buyuluZirhPreviewReflectPercent) })}</div>
                            <div>{t('skillInitialMana', { value: buyuluZirhInitialMana })}</div>
                            <div>{t('skillDurationSec', { sec: buyuluZirhDuration })}</div>
                            <div>{t('skillCooldownSec', { sec: buyuluZirhDuration })}</div>
                          </>
                        ) : skill.isBuyuluKeskinlik ? (
                          <>
                            <div>{t('skillGivesAttack', { value: isBuyuluKeskinlikActive ? buyuluKeskinlikAttackBonus : buyuluKeskinlikPreviewAttackBonus, active: skillActiveSuffix(isBuyuluKeskinlikActive) })}</div>
                            <div>{t('skillLifesteal', { value: (isBuyuluKeskinlikActive ? buyuluKeskinlikLifestealPercent : buyuluKeskinlikPreviewLifesteal).toFixed(2) })}</div>
                            <div>{t('skillInitialMana', { value: buyuluKeskinlikInitialMana })}</div>
                            <div>{t('skillDurationSec', { sec: buyuluKeskinlikDuration })}</div>
                            <div>{t('skillManaPerSec', { value: buyuluKeskinlikTickMana })}</div>
                          </>
                        ) : skill.isYasamaIsteGi ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.yasamaIsteGiDamageMin, skill.yasamaIsteGiDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 5 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '307-487' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 60 })}</div>
                          </>
                        ) : skill.isKilicCemberi ? (
                          <>
                            {renderSkillHitWithBonus(t('skillWillHit', { range: skillRangeText(skill.kilicCemberiDamageMin, skill.kilicCemberiDamageMax) }))}
                            <div>{t('skillTargetMax', { count: 10 })}</div>
                            <div>{t('skillMana', { value: skill.requiredMana ?? '230-525' })}</div>
                            <div>{t('skillDurationInstant')}</div>
                            <div>{t('skillCooldownSec', { sec: 60 })}</div>
                          </>
                        ) : (
                          renderSkillHitWithBonus(
                            t('skillDamage', {
                              min: applyDamagePercent(skill.attackMin || 0, displayedSkillDamagePercent),
                              max: applyDamagePercent(skill.attackMax || 0, displayedSkillDamagePercent),
                            })
                          )
                        )}
                        {!skill.isKilicCevirme && !skill.isHamle && !skill.isUcYonluKesme && !skill.isOfke && !skill.isDefenseBuff && !skill.isGucluVurus && !skill.isSiddetliVurus && !skill.isRuhVurusu && !skill.isKilicDarbesi && !skill.isHayaletVurus && !skill.isAlevVurusu && !skill.isKaranlikVurus && !skill.isAtesHayaleti && !skill.isKaranlikKoruma && !skill.isBuyuCozme && !skill.isParmakDarbesi && !skill.isEjderhaDonusu && !skill.isDehset && !skill.isKaranlikTas && !skill.isBuyuluZirh && !skill.isBuyuluKeskinlik && !skill.isYasamaIsteGi && !skill.isKilicCemberi && (
                          <>
                            <div>{t('skillMana', { value: skill.isBuffSkill ? previewBuffMana : (skill.requiredMana || 0) })}</div>
                            <div>{t('skillDurationSec', { sec: skill.isBuffSkill ? previewBuffDuration : (skill.effectDuration || 0) })}</div>
                            <div>{t('skillCooldownSec', { sec: skill.isBuffSkill ? previewBuffDuration : (skill.cooldown || 0) })}</div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="character-section-block">
                <h3>{t('characterEquipmentTitle')}</h3>
                <div className="equipped-list">
                  {orderedEquipment.map(({ slotType, label, item }) => (
                    <div key={`equipment-${slotType}`} className="equipped-item-row">
                      <span className="equipped-slot">{label}</span>
                      {item ? (
                        <div className="equipped-value">
                          <div
                            className="equipped-main-line"
                            style={{ color: getActivePlating(item)?.color || undefined }}
                          >
                            {item.name}
                            {typeof item.enhancement === 'number' ? ` +${item.enhancement}` : ''}
                            {item.count != null ? ` (${item.count})` : ''}
                            {getActivePlating(item) ? (
                              <span className="inventory-plating-name-tag">
                                {' '}
                                {getActivePlating(item).label}
                                {' '}
                                (+{getActivePlating(item).bonusPercent}%)
                                {' · '}
                                {formatPlatingRemaining(getActivePlating(item).remainingSec)}
                              </span>
                            ) : null}
                          </div>
                          {(() => {
                            const { statRows, efsunRows, plating } = buildEquipmentEffectRows(item);
                            if (statRows.length === 0 && efsunRows.length === 0 && !plating) {
                              return (
                                <div className="equipped-meta-line">{t('equipmentNoStats')}</div>
                              );
                            }
                            return (
                              <div className="equipped-effects">
                                {statRows.map((row) => (
                                  <div key={row.key} className="equipped-meta-line">
                                    {row.text}
                                    {row.bonusText ? (
                                      <span className={row.bonusClass || 'item-plating-stat-bonus'}> {row.bonusText}</span>
                                    ) : null}
                                  </div>
                                ))}
                                {efsunRows.map((row) => (
                                  <div key={row.key} className="equipped-meta-line equipped-meta-line--efsun">
                                    {row.label}: <span className={row.colorClass}>{row.value}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <span className="equipped-value">{tc('empty')}</span>
                      )}
                    </div>
                  ))}
                </div>
                {primaryEquipment.length === 0 && (
                  <p>{t('characterEquipmentNone')}</p>
                )}
              </div>
            </div>
  );

  if (characterPanelOnly) {
    return userCharacter ? renderCharacterPanel() : <div className="auth-loading">{tc('loading')}</div>;
  }

  return (
    <div className="auth-container auth-container--selection battle-page">
      {userCharacter && (
        <AuthLayeredCard>
          <div className="battle-nav-row">
            <button
              type="button"
              className="auth-button auth-button--compact battle-nav-btn battle-nav-btn--back"
              style={authButtonSmallStyle}
              onClick={handleBattleBack}
              aria-label={tc('back')}
            >
              ←
            </button>
            <button
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'battle' ? ' battle-subtab--active' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => setActiveTab('battle')}
              data-tour="battle-battle-tab"
            >
              {t('navBattle')}
            </button>
            <button
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'character' ? ' battle-subtab--active' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => setActiveTab('character')}
            >
              {t('navCharacter')}
            </button>
            <button
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'map' ? ' battle-subtab--active' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => setActiveTab('map')}
              data-tour="battle-map-tab"
            >
              {t('navMap')}
              {!mapHintSeen && <span className="map-tab-hint-dot" aria-hidden="true" />}
            </button>
            <button
              ref={depotBtnRef}
              data-depot-button
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${depotItemCount > 0 ? ' depot-btn--alert' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => navigate('/inventory?tab=pending', { state: { from: '/battle' } })}
            >
              {depotItemCount > 0
                ? ti('tabsStorageCount', { count: depotItemCount })
                : ti('tabsStorage')}
            </button>
          </div>
          <p className="battle-village-line">
            {dungeonActive ? (
              // Zindanda köy satırının yerinde (aynı yükseklik): aşama + geri sayım
              <DungeonHud dungeon={dungeon} clockOffset={dungeonClockOffset} td={td} />
            ) : t('villageLine', {
              village: translateVillageName(tg, userCharacter.villageName),
              x: positionX,
              y: positionY,
            })}
          </p>

          <div className="auth-selection-body battle-body">
          {activeTab === 'battle' && (
            <>
              <div className="monsters-section monsters-section--arena battle-stat-panel" style={authStatPanelStyle}>
                <div className="monsters-header">
                  <div className="monsters-title-row">
                    <div className={`level-bar-compact${expGainEnabled ? '' : ' level-bar-compact--exp-off'}`}>
                      <span className="exp-gain-toggle-wrap">
                        {expBlockedFloats.map((d) => (
                          <span key={d.id} className="exp-blocked-float">+{d.value} EXP</span>
                        ))}
                      <button
                        type="button"
                        className={`exp-gain-toggle${expGainEnabled ? ' is-on' : ' is-off'}${expBlockedFloats.length ? ' is-absorbing' : ''}`}
                        onClick={toggleExpGain}
                        aria-pressed={expGainEnabled}
                        title={expGainEnabled ? t('expGainOnTitle') : t('expGainOffTitle')}
                      >
                        {expGainEnabled ? t('expGainOn') : t('expGainOff')}
                      </button>
                      </span>
                      <span className="level-text-compact">Lv.{userCharacter?.level || 1}</span>
                      <div className="level-bar-wrap">
                        {expBarGainFloats.map((d) => (
                          <div key={d.id} className="exp-bar-gain-float">
                            +{d.value} EXP
                          </div>
                        ))}
                        <div className="level-bar-container">
                        <div
                          className="level-bar-fill"
                          style={{
                            width: `${userCharacter?.expToNextLevel > 0
                              ? ((userCharacter.currentLevelExp || 0) / ((userCharacter.currentLevelExp || 0) + userCharacter.expToNextLevel)) * 100
                              : 100}%`
                          }}
                        />
                        <div className="level-bar-text">
                          {expShowPercent
                            ? (() => {
                              const current = userCharacter?.currentLevelExp || 0;
                              const total = current + (userCharacter?.expToNextLevel || 0);
                              const pct = total > 0 ? (current / total) * 100 : 100;
                              return `${pct.toFixed(1)}%`;
                            })()
                            : `${userCharacter?.currentLevelExp || 0} / ${(userCharacter?.currentLevelExp || 0) + (userCharacter?.expToNextLevel || 0)} EXP`}
                        </div>
                        <button
                          type="button"
                          className="exp-display-toggle"
                          onClick={() => {
                            setExpShowPercent((prev) => {
                              const next = !prev;
                              try {
                                localStorage.setItem('battleExpShowPercent', next ? '1' : '0');
                              } catch { /* ignore */ }
                              return next;
                            });
                          }}
                          title={expShowPercent ? tl('EXP miktarını göster', 'Show EXP amount') : tl('EXP yüzdesini göster', 'Show EXP percentage')}
                          aria-label={expShowPercent ? tl('EXP miktarını göster', 'Show EXP amount') : tl('EXP yüzdesini göster', 'Show EXP percentage')}
                        >
                          {expShowPercent ? '123' : '%'}
                        </button>
                      </div>
                      </div>
                    </div>
                    {activeMarketBuffs.length > 0 && (
                      // Market güçlendirmeleri tek küçük düğmede; ayrıntı tıklayınca açılır.
                      <button
                        type="button"
                        className="market-buff-badge market-buff-badge--compact"
                        onClick={() => setMarketBuffPopupOpen(true)}
                        title={activeMarketBuffs.map((b) => b.shortLabel).join(' · ')}
                        aria-label={activeMarketBuffs.map((b) => b.shortLabel).join(' · ')}
                      >
                        ⚡{activeMarketBuffs.length}
                      </button>
                    )}
                    {activeAdBuffs.length > 0 && (
                      // Video ile alınan bonuslar ayrı düğmede; aynı bonus penceresini açar.
                      <button
                        type="button"
                        className="market-buff-badge market-buff-badge--compact market-buff-badge--video"
                        onClick={() => setMarketBuffPopupOpen(true)}
                        title={activeAdBuffs.map((b) => b.shortLabel).join(' · ')}
                        aria-label={activeAdBuffs.map((b) => b.shortLabel).join(' · ')}
                      >
                        🎬{activeAdBuffs.length}
                      </button>
                    )}
                  </div>
                </div>
                {battleReconnecting && (
                  <div className="battle-connection-banner">
                    {t('reconnecting')}
                  </div>
                )}
                <div className="monsters-arena-wrap">
                  <BattleArenaBackground
                    imageUrl={currentVillageBattleBg}
                    offsetPx={battleBgOffsetPx}
                    rewinding={battleBgRewinding}
                    onMaxOffsetChange={handleBattleBgMaxOffsetChange}
                  />
                  <BattleArena
                    monsters={arenaMonsters}
                    monsterDamageFloats={monsterDamageFloats}
                    characterBattleSprites={characterBattleSprites}
                    characterAnim={characterAnim}
                    characterWalkDir={characterWalkDir}
                    characterHP={displayCharacterHP}
                    characterMaxHP={characterMaxHP}
                    characterMP={characterMP}
                    characterMaxMP={characterMaxMP}
                    characterHpFloats={arenaHpFloats}
                    characterMpFloats={arenaMpFloats}
                    characterHpPending={characterDeathActive ? 0 : totalPendingHP}
                    characterMpPending={totalPendingMP}
                    characterDeathActive={characterDeathActive}
                    characterLabel={userCharacter?.characterName || 'K'}
                    characterAnchorRef={characterAnchorRef}
                    skillVisualEffects={skillVisualEffects}
                    onSkillEffectComplete={removeSkillVisualEffect}
                    onApproachComplete={stableApproachComplete}
                    onMonsterDeathComplete={stableDeathComplete}
                  />
                  {isVillage3WaterBattle && (
                    <div className="battle-water-overlay" aria-hidden="true" />
                  )}
                  <span className="monster-count" title={t('monstersTotal', { count: monsterInstances.length })}>
                    {monsterInstances.length}
                  </span>
                </div>
              </div>

              {!dungeonActive && (
                <div className="battle-walk-row" data-tour="battle-walk">
                  <button
                    type="button"
                    className="battle-walk-btn"
                    {...walkHoldProps(-1)}
                    disabled={mapWalkStep <= 0 || characterDeathActive}
                    aria-label={t('walkBack')}
                  >
                    ◀
                  </button>
                  <div className="battle-walk-track" aria-hidden="true">
                    <div className="battle-walk-fill" style={{ width: `${mapWalkStep}%` }} />
                    <span className="battle-walk-label">{t('walkProgress', { step: mapWalkStep, total: MAP_WALK_STEPS })}</span>
                  </div>
                  <button
                    type="button"
                    className="battle-walk-btn"
                    {...walkHoldProps(1)}
                    disabled={mapWalkStep >= MAP_WALK_STEPS || characterDeathActive}
                    aria-label={t('walkForward')}
                  >
                    ▶
                  </button>
                </div>
              )}
              <div className="drops-line-below-monsters battle-stat-panel" style={authStatPanelStyle}>
                <div className="drops-marquee-wrap">
                  <span className={`drops-marquee-text ${latestDropText ? 'drops-marquee-animate' : ''}`}>
                    {latestDropText ? `${latestDropText}  ——  ${latestDropText}` : t('dropsNone')}
                  </span>
                </div>
                <button
                  type="button"
                  className="auth-button auth-button--compact drops-line-button"
                  style={authButtonStyle}
                  onClick={() => setDropsPopupOpen(true)}
                >
                  {t('dropsButton')}
                </button>
              </div>
              <div
                className="character-stats-compact battle-stat-panel"
                style={authStatPanelStyle}
              >
                <div className="buff-indicators-area">
                  <button
                    type="button"
                    className={`battle-bonuses-btn${activeBattleBuffs.length === 0 ? ' battle-bonuses-btn--empty' : ''}`}
                    onClick={() => setBattleBonusesPopupOpen(true)}
                  >
                    {activeBattleBuffs.length > 0
                      ? t('battleBonusesButton', { count: activeBattleBuffs.length })
                      : t('buffNone')}
                  </button>
                </div>
                <div className="normal-attack-display">
                  {t('attackNormal', { min: displayedNormalAttackMinWithFish, max: displayedNormalAttackMaxWithFish })}
                  {equippedWeapon && (
                    <span className="weapon-sublabel"> {t('attackWeaponIncluded')}</span>
                  )}
                  <span className="weapon-sublabel"> | {t('defenseLabel', { value: displayedDefenseWithFish })}</span>
                </div>
              </div>
              <div className="battle-actions">
                <div className="action-buttons">
                {activeTab === 'battle' && !dungeonActive && (
                <button
                  type="button"
                  className="auth-button pull-monsters-button"
                  style={pullMonstersButtonStyle}
                  onClick={handlePullMonsters}
                  disabled={!canUsePullMonsters}
                  title={
                    characterDeathActive
                      ? t('pullMonstersRespawning')
                      : !battleAuthed
                        ? t('pullMonstersNotReady')
                        : battleReconnecting
                          ? t('pullMonstersNotReady')
                          : t('pullMonsters')
                  }
                  aria-label={t('pullMonsters')}
                >
                  {t('pullMonsters')}
                </button>
            )}
                  <button
                    type="button"
                    className={`auth-button attack-button ${normalAttackCooldownUntil > Date.now() ? 'disabled' : ''}${dungeonActive ? ' attack-button--dungeon' : ''}`}
                    style={dungeonActive ? { ...authButtonStyle, gridColumn: '1 / -1' } : authButtonStyle}
                    onClick={handleNormalAttack}
                    disabled={normalAttackCooldownUntil > Date.now()}
                    title={normalAttackRemainingSec > 0 ? t('normalAttackWait', { sec: normalAttackRemainingSec }) : t('normalAttackTooltip')}
                  >
                    {normalAttackRemainingSec > 0 ? t('attackCooldown', { sec: normalAttackRemainingSec }) : t('attackButton')}
                  </button>
                </div>
                <div className="action-buttons-1">
            

                  {userCharacter && userCharacter.level < 5 ? (
                    <div className="skill-locked-message">
                      {t('skillsLocked')}
                    </div>
                  ) : (
                    skills.map((skill) => {
                      const isOnCooldown = skillCooldowns[skill.id] && skillCooldowns[skill.id] > Date.now();
                      const skillRemainingSec = isOnCooldown ? Math.max(0, Math.ceil((skillCooldowns[skill.id] - Date.now()) / 1000)) : 0;
                      const minMana = skill.isBuffSkill && !skill.isOfke && !skill.isDefenseBuff
                        ? 110
                        : skill.isOfke
                          ? ofkeMana
                          : skill.isDefenseBuff
                            ? defenseBuffMana
                            : skill.isAtesHayaleti
                              ? fireGhostInitialMana
                              : skill.isKaranlikKoruma
                                ? darkProtectionInitialMana
                                : skill.isBuyuluKeskinlik
                                  ? buyuluKeskinlikInitialMana
                                  : skill.isBuyuluZirh
                                    ? buyuluZirhInitialMana
                                : (skill.requiredMana || 0);
                      const hasEnoughMana = characterMP >= minMana;
                      const isBuffActiveAndThisIsBuff = skill.isBuffSkill && !skill.isOfke && !skill.isDefenseBuff && (attackBuffUntil > Date.now());
                      const isRageActiveAndThisIsOfke = skill.isOfke && (rageSpeedBuffUntil > Date.now());
                      const isDefenseBuffActiveAndThis = skill.isDefenseBuff && (defenseBuffUntil > Date.now());
                      const isFireGhostActiveAndThis = skill.isAtesHayaleti && isFireGhostActive;
                      const isDarkProtectionActiveAndThis = skill.isKaranlikKoruma && isDarkProtectionActive;
                      const isBuyuluKeskinlikActiveAndThis = skill.isBuyuluKeskinlik && isBuyuluKeskinlikActive;
                      const isBuyuluZirhActiveAndThis = skill.isBuyuluZirh && isBuyuluZirhActive;
                      const isDehsetActiveAndThis = skill.isDehset && isDehsetActive;
                      const canUse = !isOnCooldown && !isBuffActiveAndThisIsBuff && !isRageActiveAndThisIsOfke && !isDefenseBuffActiveAndThis && !isFireGhostActiveAndThis && !isDarkProtectionActiveAndThis && !isBuyuluKeskinlikActiveAndThis && !isBuyuluZirhActiveAndThis && !isDehsetActiveAndThis && hasEnoughMana;
                      const displayName = skill.skillName || t('skillFallback', { id: skill.id });

                      return (
                        <button
                          key={skill.id}
                          className={`skill-button ${!canUse ? 'disabled' : ''}`}
                          onClick={() => canUse && handleUseSkill(skill.id)}
                          disabled={!canUse}
                          aria-label={displayName}
                          title={isBuffActiveAndThisIsBuff ? t('skillTitleBuffActive', { bonus: attackBuffBonus }) : isRageActiveAndThisIsOfke ? t('skillTitleRageActive', { speed: rageSpeedBuffValue }) : isDefenseBuffActiveAndThis ? t('skillTitleDefenseBuffActive', { defense: defenseBuffValue }) : isFireGhostActiveAndThis ? t('skillTitleFireGhostActive', { damage: fireGhostTickDamage }) : isDarkProtectionActiveAndThis ? t('skillTitleDarkProtectionActive', { percent: Math.round(darkProtectionPercent), defense: darkProtectionDefenseBonus }) : isBuyuluKeskinlikActiveAndThis ? t('skillTitleBuyuluKeskinlikActive', { attack: buyuluKeskinlikAttackBonus, lifesteal: Math.round(buyuluKeskinlikLifestealPercent) }) : isBuyuluZirhActiveAndThis ? t('skillTitleBuyuluZirhActive', { defense: buyuluZirhDefenseBonus, reflect: Math.round(buyuluZirhReflectPercent) }) : isDehsetActiveAndThis ? t('skillTitleDehsetActive', { percent: Number(dehsetReducePercent).toFixed(1) }) : isOnCooldown ? t('skillsCooldown', { sec: skillRemainingSec }) : !hasEnoughMana ? t('skillsInsufficientMana', { mana: minMana }) : skill.skillName}
                        >
                          {skill.imageUrl && !brokenSkillImages[skill.id] ? (
                            <img
                              className="skill-button-image"
                              src={normalizeSkillImageUrl(skill.imageUrl)}
                              alt={displayName}
                              onError={() => setBrokenSkillImages(prev => ({ ...prev, [skill.id]: true }))}
                            />
                          ) : (
                            <span className="skill-button-fallback">{getSkillImageLabel(skill.imageUrl)}</span>
                          )}
                          {isOnCooldown && skillRemainingSec > 0 && (
                            <span className="skill-button-cooldown">{skillRemainingSec}s</span>
                          )}
                          {!canUse && !isOnCooldown && !hasEnoughMana && minMana > 0 && (
                            <span className="skill-button-mana-cost">{minMana} SP</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="pots-section">
                  <div className="pots-grid pots-grid--squares">
                    {BATTLE_POT_SLOTS.map((slotType, slotIndex) => {
                      const pot = pots.find(p => p.slotType === slotType);
                      const potVisual = potVisualRef.current[slotType] || {};
                      const potImage = pot?.imageUrl || potVisual.imageUrl;
                      const displayName = pot?.name || potVisual.name || t('potsEmptySlot');
                      const count = pot?.count || 0;
                      const potTrName = originalGameName(pot?.name || potVisual.name || '');
                      const isHPPot = potTrName && (potTrName.toLowerCase().includes('kırmızı') || potTrName.toLowerCase().includes('kirmizi'));
                      const isMPPot = potTrName && potTrName.toLowerCase().includes('mavi');
                      const wouldOverflowHP = isHPPot && (characterHP + totalPendingHP >= characterMaxHP);
                      const wouldOverflowMP = isMPPot && (characterMP + totalPendingMP >= characterMaxMP);
                      const canUse = pot && !wouldOverflowHP && !wouldOverflowMP;

                      return (
                        <button
                          key={slotType}
                          type="button"
                          className={`pot-square ${!canUse ? 'disabled' : ''} ${!pot || count <= 0 ? 'empty' : ''}`}
                          onClick={() => canUse && handleUsePot(slotType)}
                          disabled={!canUse}
                          title={
                            !pot ? tc('empty') :
                            wouldOverflowHP ? t('potsHpFull', { name: displayName }) :
                            wouldOverflowMP ? t('potsMpFull', { name: displayName }) :
                            t('potsCount', { name: displayName, count })
                          }
                          aria-label={pot && count > 0 ? t('potsCount', { name: displayName, count }) : t('potsEmptySlot')}
                        >
                          {pot && count > 0 && potImage ? (
                            <img src={`/${potImage}`} alt={displayName} draggable={false} />
                          ) : (
                            <span className="pot-square-index">{slotIndex + 1}</span>
                          )}
                          {count > 0 && <span className="pot-square-count">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {dungeonActive ? (
                  <button
                    type="button"
                    className="auth-button dungeon-exit-button"
                    style={authButtonSmallStyle}
                    onClick={() => setDungeonExitOpen(true)}
                  >
                    {td('exit')}
                  </button>
                ) : null}
              </div>
            </>
          )}

          {activeTab === 'character' && renderCharacterPanel()}

          {activeTab === 'map' && (
            <div className="map-tab-panel">
              <p className="map-help-text">
                {t('mapHelp')}
              </p>
              <div
                ref={mapRef}
                className="battle-map"
                style={mapPanelStyle}
                onClick={handleMapClick}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!mapRef.current) return;
                    const rect = mapRef.current.getBoundingClientRect();
                    handleMapClick({ clientX: rect.left, clientY: rect.top });
                  }
                }}
              >
                {mapTrail && (
                  <svg key={`trail-${mapTrail.key}`} className="map-path" viewBox="0 0 300 300" preserveAspectRatio="none" aria-hidden="true">
                    <line x1={mapTrail.fromX} y1={mapTrail.fromY} x2={mapTrail.toX} y2={mapTrail.toY} />
                  </svg>
                )}
                {mapTrail && (
                  <div
                    key={`ping-${mapTrail.key}`}
                    className="map-click-ping"
                    style={{ left: `${(mapTrail.toX / 300) * 100}%`, top: `${(mapTrail.toY / 300) * 100}%` }}
                  />
                )}
                {!mapHintSeen && (
                  <div className="map-hint" aria-hidden="true">
                    <div className="map-hint-target" />
                    <div className="map-hint-hand">👆</div>
                    <div className="map-hint-label">{t('mapClickHint')}</div>
                  </div>
                )}
                <div
                  className="map-marker"
                  style={{
                    left: `${(positionX / 300) * 100}%`,
                    top: `${(positionY / 300) * 100}%`,
                  }}
                />
              </div>
              <div className="map-footer">
                <span>{t('mapSelectedPosition', { x: positionX, y: positionY })}</span>
                <span>{t('mapMultiplier', { key: monsterKey })}</span>
              </div>
            </div>
          )}

          </div>
        </AuthLayeredCard>
      )}

      <Coachmark
        tourId="battle"
        enabled={activeTab === 'battle' && !encounterModalOpen}
        steps={BATTLE_TOUR_STEPS}
      />
      <Coachmark
        tourId="battleMap"
        enabled={activeTab === 'map'}
        steps={[
          { selector: '.battle-map', textKey: 'mapTap' },
          { selector: '.battle-map', textKey: 'mapZones' },
          { selector: '.map-footer', textKey: 'mapMultiplier' },
          { selector: '[data-tour="battle-battle-tab"]', textKey: 'mapBackToBattle' },
        ]}
      />
      <Coachmark
        tourId="encounter"
        enabled={Boolean(encounterModalOpen && encounterOffer)}
        steps={ENCOUNTER_TOUR_STEPS}
      />

      {/* Metin / Boss teklifi */}
      {expGainNotice && (
        <div className="popup-overlay exp-gain-notice-overlay" onClick={() => setExpGainNotice(null)}>
          <div className="popup-content popup-content-small auth-card exp-gain-notice" style={authCardStyle} role="alertdialog" aria-modal="true">
            <div className="popup-body">
              <p className={`exp-gain-notice__title${expGainNotice === 'off' ? ' is-off' : ' is-on'}`}>
                {expGainNotice === 'off' ? t('expGainNoticeOffTitle') : t('expGainNoticeOnTitle')}
              </p>
              <p className="exp-gain-notice__text">
                {expGainNotice === 'off' ? t('expGainNoticeOffText') : t('expGainNoticeOnText')}
              </p>
            </div>
            <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center' }}>
              <button type="button" className="auth-button popup-button" style={authButtonStyle} onClick={() => setExpGainNotice(null)}>
                {tc('ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {battleExitOpen && (
        <div className="popup-overlay">
          <div className="popup-content auth-card" style={authCardStyle} role="alertdialog" aria-modal="true">
            <div className="popup-header">
              <h3>{td('exit')}</h3>
            </div>
            <div className="popup-body">
              <p>{tl('Savaştan çıkmak istediğine emin misin? Alandaki tüm canavarlar ve taşlar silinecek.', 'Are you sure you want to leave the battle? All monsters and stones on the field will be removed.')}</p>
            </div>
            <div className="popup-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button type="button" className="auth-button popup-button" style={authButtonStyle} onClick={handleBattleExit}>
                {td('yes')}
              </button>
              <button type="button" className="auth-button popup-button" style={authButtonSmallStyle} onClick={() => setBattleExitOpen(false)}>
                {td('no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {dungeonExitOpen && (
        <div className="popup-overlay">
          <div className="popup-content auth-card" style={authCardStyle} role="alertdialog" aria-modal="true">
            <div className="popup-header">
              <h3>{td('exit')}</h3>
            </div>
            <div className="popup-body">
              <p>{td('exitConfirm')}</p>
            </div>
            <div className="popup-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button type="button" className="auth-button popup-button" style={authButtonStyle} onClick={handleDungeonExit}>
                {td('yes')}
              </button>
              <button type="button" className="auth-button popup-button" style={authButtonSmallStyle} onClick={() => setDungeonExitOpen(false)}>
                {td('no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {dungeonResult && (
        <div className="popup-overlay">
          <div className="popup-content auth-card" style={authCardStyle} role="dialog" aria-modal="true">
            <div className="popup-header">
              <h3>
                {dungeonResult.result === 'completed' ? td('resultCompleted')
                  : dungeonResult.result === 'timeout' ? td('resultTimeout')
                    : dungeonResult.result === 'dead' ? td('resultDead') : td('resultAbandoned')}
              </h3>
            </div>
            <div className="popup-body" style={{ textAlign: 'center' }}>
              {dungeonResult.result === 'completed' ? (
                <>
                  <img
                    className="dungeon-result-chest"
                    src={`/image/others/zindan_sandigi_${dungeonResult.key}.png`}
                    alt=""
                  />
                  <p>{td('resultCompletedText', { chest: td('chest') })}</p>
                </>
              ) : (
                <p>
                  {dungeonResult.result === 'timeout' ? td('resultTimeoutText')
                    : dungeonResult.result === 'dead' ? td('resultDeadText') : td('resultAbandonedText')}
                </p>
              )}
            </div>
            <div className="popup-footer" style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={() => {
                  setDungeonResult(null);
                  setDungeon(null);
                  navigate('/dungeon');
                }}
              >
                {td('backToDungeons')}
              </button>
            </div>
          </div>
        </div>
      )}

      {encounterModalOpen && encounterOffer && (
        <div className="popup-overlay">
          <div
            className="popup-content auth-card"
            style={authCardStyle}
          >
            <div className="popup-header">
              <h3>{encounterOffer.name}</h3>
            </div>
            <div className="popup-body">
              <p>
                {encounterOffer.kind === 'boss'
                  ? t('encounterBoss')
                  : t('encounterMetin')}
              </p>
              <p>{t('encounterLevel', { level: encounterOffer.level })}</p>
            </div>
            <div className="popup-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={handleAcceptEncounter}
                data-tour="encounter-fight"
              >
                {t('encounterFight')}
              </button>
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonSmallStyle}
                onClick={handleDeclineEncounter}
                data-tour="encounter-decline"
              >
                {t('encounterSkip')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Düşenler popup - butona tıklayınca açılır */}
      {dropsPopupOpen && (
        <div className="popup-overlay" onClick={() => setDropsPopupOpen(false)}>
          <div
            className="popup-content popup-drops-list auth-card"
            style={authCardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header">
              <h3>{t('dropsPopupTitle')}</h3>
            </div>
            <div className="popup-body popup-drops-scroll">
              {dropHistory.length === 0 ? (
                <div className="drop-line">{t('dropsNone')}</div>
              ) : (
                [...dropHistory].reverse().map((evt) => (
                  <div key={evt.id} className="drop-event-block">
                    {evt.gold > 0 && <div className="drop-line">{evt.gold} Yang</div>}
                    {(evt.items || []).map((it, i) => (
                      <div key={it.id || i} className="drop-line">{it.itemName} x{it.count || 1}</div>
                    ))}
                    {evt.exp > 0 && <div className="drop-line">{evt.exp} EXP</div>}
                  </div>
                ))
              )}
            </div>
            <div className="popup-footer">
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={() => setDropsPopupOpen(false)}
              >
                {tc('ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {marketBuffPopupOpen && (
        <div className="popup-overlay" onClick={() => setMarketBuffPopupOpen(false)}>
          <div
            className="popup-content popup-content-small auth-card market-buff-popup"
            style={authCardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header">
              <h3>{t('marketBuffTitle')}</h3>
            </div>
            <div className="popup-body">
              {activeMarketBuffs.length === 0 && activeAdBuffs.length === 0 ? (
                <div className="market-buff-popup-line">{t('marketBuffNone')}</div>
              ) : (
                activeMarketBuffs.map((buff) => (
                  <div key={buff.key} className="market-buff-popup-line">
                    <strong>{buff.title}</strong>
                    <span>{buff.effect}</span>
                    <span>{t('marketBuffRemaining', { time: buff.remaining })}</span>
                  </div>
                ))
              )}
              {activeAdBuffs.length > 0 ? (
                <>
                  <div className="market-buff-popup-section">{tl('🎬 Video bonusları', '🎬 Video bonuses')}</div>
                  {activeAdBuffs.map((buff) => (
                    <div key={buff.key} className="market-buff-popup-line market-buff-popup-line--video">
                      <strong>{buff.title}</strong>
                      <span>{buff.effect}</span>
                      <span>{t('marketBuffRemaining', { time: buff.remaining })}</span>
                    </div>
                  ))}
                </>
              ) : null}
            </div>
            <div className="popup-footer">
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={() => setMarketBuffPopupOpen(false)}
              >
                {tc('ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {battleBonusesPopupOpen && (
        <div className="popup-overlay" onClick={() => setBattleBonusesPopupOpen(false)}>
          <div
            className="popup-content popup-content-small auth-card battle-bonuses-popup"
            style={authCardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header">
              <h3>{t('battleBonusesTitle')}</h3>
            </div>
            <div className="popup-body battle-bonuses-popup-body">
              {activeBattleBuffs.length === 0 ? (
                <div className="battle-bonus-popup-line battle-bonus-popup-line--empty">{t('buffNone')}</div>
              ) : (
                activeBattleBuffs.map((buff) => (
                  <div
                    key={buff.key}
                    className={`battle-bonus-popup-line battle-bonus-popup-line--${buff.variant}`}
                  >
                    {buff.label}
                  </div>
                ))
              )}
            </div>
            <div className="popup-footer">
              <button
                type="button"
                className="auth-button popup-button"
                style={authButtonStyle}
                onClick={() => setBattleBonusesPopupOpen(false)}
              >
                {tc('ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drop fly + bildirimleri */}
      {dropFlyItems.map((item) => (
        <img
          key={item.id}
          className="battle-drop-fly"
          src={item.imageUrl}
          alt=""
          aria-hidden="true"
          style={{
            '--fly-start-x': `${item.startX}px`,
            '--fly-start-y': `${item.startY}px`,
            '--fly-delta-x': `${item.deltaX}px`,
            '--fly-delta-y': `${item.deltaY}px`,
          }}
        />
      ))}
      {dropNotifications.length > 0 && (
        <div className="drop-notifications-container">
          {dropNotifications.slice(-3).map((notification) => (
            <div key={notification.id} className={`drop-notification drop-notification--${notification.type || 'warning'}`}>
              {notification.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatDungeonTime = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

/**
 * Zindan geri sayımı kendi bileşeninde sayar: saniyelik güncelleme yalnızca bu kutuyu
 * yeniden çizer, savaş ekranını (canavar dizilimi, animasyonlar) etkilemez.
 */
const DungeonHud = React.memo(function DungeonHud({ dungeon, clockOffset, td }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!dungeon?.endsAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [dungeon?.endsAt]);
  const secondsLeft = dungeon?.endsAt
    ? Math.max(0, Math.ceil((dungeon.endsAt - (now + clockOffset)) / 1000))
    : null;
  return (
    <span className="dungeon-hud" aria-live="polite">
      <span className="dungeon-hud-phase">
        {dungeon.phase === 'waves'
          ? td('phaseWaves', { wave: dungeon.wave, total: dungeon.waveCount })
          : dungeon.phase === 'metin'
            ? td('phaseMetin')
            : dungeon.phase === 'boss'
              ? td('phaseBoss')
              : td('phaseWaiting')}
      </span>
      {secondsLeft != null ? (
        <span className={`dungeon-hud-time${secondsLeft <= 30 ? ' is-urgent' : ''}`}>
          {formatDungeonTime(secondsLeft)}
        </span>
      ) : null}
    </span>
  );
});

// Arena ekranındaki karakter sekmesi: savaş ekranındaki panelin aynısı.
export function BattleCharacterPanel() {
  return <Battle characterPanelOnly />;
}

export default function BattleRoute() {
  const location = useLocation();
  const { t } = useTranslation('common');
  const username = localStorage.getItem('username') || '';
  const arenaOfflineFight = location.state?.arenaOfflineFight;
  const arenaOnlineMatch = location.state?.arenaOnlineMatch;
  const explicit = Boolean(arenaOfflineFight || arenaOnlineMatch);
  const [resumeId, setResumeId] = useState('');
  const [checked, setChecked] = useState(explicit);

  useEffect(() => {
    if (explicit) {
      setChecked(true);
      return undefined;
    }
    let alive = true;
    setChecked(false);
    (async () => {
      try {
        const matchId = await fetchActiveArenaMatchId(username);
        if (alive) setResumeId(matchId || '');
      } catch {
        if (alive) setResumeId('');
      } finally {
        if (alive) setChecked(true);
      }
    })();
    return () => { alive = false; };
  }, [explicit, username]);

  if (arenaOfflineFight || arenaOnlineMatch) {
    return (
      <ArenaBattleScreen
        offlineFight={arenaOfflineFight || null}
        onlineMatch={arenaOnlineMatch || null}
      />
    );
  }
  if (!checked) {
    return <p className="arena-empty">{t('loading')}</p>;
  }
  if (resumeId) {
    return (
      <ArenaBattleScreen
        offlineFight={null}
        onlineMatch={{ matchId: resumeId }}
      />
    );
  }
  return <Battle />;
}

